// Configuration unifiée pour admin-web et app mobile
import { API_BASE_URL, API_CONFIG } from '../config/api.config';
import { publicJsonPost } from './publicJsonPost';
import {
  clearAssistanceSession,
  ensureAssistanceSessionFresh,
  isAssistanceActive,
  peekAssistanceSession,
  startAssistanceSession,
} from '../utils/assistanceSession';
import { buildPosDeviceHeaders, shouldAttachPosDeviceHeader } from '../utils/posCaisseDeviceHeader';

const ACCESS_TOKEN_KEY = 'admin_access_token';
const REFRESH_TOKEN_KEY = 'admin_refresh_token';
const USER_KEY = 'admin_user';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.refreshPromise = null;
  }

  /**
   * Token Bearer effectif : assistance (sessionStorage) si actif et non expiré,
   * sinon JWT Super Admin (localStorage). Purge synchrone si expiresAt dépassé.
   * Si une session assistance vient d'expirer, retourne null (pas de fallback SA
   * silencieux — le caller doit traiter ASSISTANCE_EXPIRED).
   */
  getAccessToken() {
    const peeked = peekAssistanceSession();
    if (peeked) {
      const fresh = ensureAssistanceSessionFresh();
      if (!fresh) {
        // Session présente mais expirée → ne pas masquer avec le JWT SA.
        return null;
      }
      return fresh.token;
    }
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /** JWT SA uniquement — jamais le token assistance. */
  getSuperAdminAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  isAuthenticated() {
    return !!this.getSuperAdminAccessToken() || !!this.getRefreshToken() || isAssistanceActive();
  }

  isAssistanceMode() {
    return isAssistanceActive();
  }

  setTokens(data) {
    const { accessToken, refreshToken } = data || {};
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Legacy keys
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    clearAssistanceSession();
  }

  async refreshTokens() {
    // A1.2 — le token assistance n'est pas renouvelable ; ne jamais refresh dessus.
    if (isAssistanceActive()) {
      throw new Error('ASSISTANCE_NO_REFRESH');
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      throw new Error('Refresh token manquant. Veuillez vous reconnecter.');
    }

    this.refreshPromise = fetch(`${this.baseURL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error?.error?.message || 'Refresh token invalide.');
        }
        return response.json();
      })
      .then((data) => {
        this.setTokens(data);
        if (data.user) {
          // Merger : le refresh JWT ne doit pas effacer platformPermissions / platformProfile.
          let previous = {};
          try {
            previous = JSON.parse(localStorage.getItem(USER_KEY) || '{}') || {};
          } catch {
            previous = {};
          }
          const merged = {
            ...previous,
            ...data.user,
            platformPermissions: Array.isArray(data.user.platformPermissions)
              ? data.user.platformPermissions
              : previous.platformPermissions,
            platformProfile: data.user.platformProfile !== undefined
              ? data.user.platformProfile
              : previous.platformProfile,
          };
          localStorage.setItem(USER_KEY, JSON.stringify(merged));
          try {
            window.dispatchEvent(new CustomEvent('authUserUpdated', { detail: merged }));
          } catch {
            /* ignore */
          }
        }
        return data;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  async request(endpoint, options = {}, retry = true) {
    // A1.2 — si une session assistance existe mais est expirée : purge + refus
    // explicite (jamais de fallback silencieux sur le JWT SA).
    const peeked = peekAssistanceSession();
    if (peeked && !ensureAssistanceSessionFresh()) {
      const err = new Error('ASSISTANCE_EXPIRED');
      err.code = 'ASSISTANCE_EXPIRED';
      throw err;
    }

    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    const inAssistance = isAssistanceActive();

    if (!token && !refreshToken) {
      console.error('❌ [API] NOT_AUTHENTICATED - Pas de token');
      throw new Error('NOT_AUTHENTICATED');
    }

    // Assistance active mais token null (ne devrait pas arriver) → fail-closed
    if (inAssistance && !token) {
      clearAssistanceSession();
      const err = new Error('ASSISTANCE_EXPIRED');
      err.code = 'ASSISTANCE_EXPIRED';
      throw err;
    }

    const isFormData = options?.body instanceof FormData;

    const method = String(options.method || 'GET').toUpperCase();
    const posDeviceHeaders = shouldAttachPosDeviceHeader(endpoint, method)
      ? buildPosDeviceHeaders()
      : {};
    const config = {
      ...options,
      headers: {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        'Cache-Control': 'no-cache, no-store, max-age=0',
        Pragma: 'no-cache',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...posDeviceHeaders,
        ...(options.headers || {}),
      },
      cache: 'no-store',
    };

    if (
      !config.signal &&
      method === 'GET' &&
      typeof AbortSignal !== 'undefined' &&
      typeof AbortSignal.timeout === 'function'
    ) {
      config.signal = AbortSignal.timeout(API_CONFIG.TIMEOUT);
    }

    try {
      const response = await fetch(url, config);

      if (response.status === 304) {
        throw new Error('NOT_MODIFIED');
      }

      if (!response.ok) {
        // A1.2 — en assistance : pas de refresh ; session assistance invalidée.
        if (response.status === 401 && inAssistance) {
          clearAssistanceSession();
          const err = new Error('ASSISTANCE_EXPIRED');
          err.code = 'ASSISTANCE_EXPIRED';
          throw err;
        }

        if (response.status === 401 && retry && this.getRefreshToken() && !inAssistance) {
          try {
            await this.refreshTokens();
            return this.request(endpoint, options, false);
          } catch (refreshError) {
            this.clearTokens();
            if (!window.location.pathname.includes('/login')) {
              setTimeout(() => {
                window.location.href = '/login';
              }, 500);
            }
            throw refreshError;
          }
        }

        const contentType = response.headers.get('content-type') || '';
        let errorData = {};
        if (contentType.includes('application/json')) {
          errorData = await response.json().catch(() => ({}));
        } else {
          const text = await response.text();
          errorData = { error: { message: text } };
        }

        // Préférer `detail` / `details` métier au libellé générique `error`.
        const message =
          (typeof errorData?.detail === 'string' && errorData.detail.trim()
            ? errorData.detail
            : null) ||
          (typeof errorData?.details === 'string' && errorData.details.trim()
            ? errorData.details
            : null) ||
          errorData?.message ||
          errorData?.details?.[0]?.message ||
          errorData?.error?.message ||
          (typeof errorData?.error === 'string' ? errorData.error : null) ||
          `Erreur ${response.status}`;
        const error = new Error(message);
        error.status = response.status;
        error.data = errorData;
        // Remonte le code métier S4 (ex. POLITIQUE_PAIEMENT_ACTIVATION_DESACTIVEE)
        error.code = errorData?.code || errorData?.error?.code || undefined;
        error.response = { status: response.status, data: errorData };
        throw error;
      }

      if (response.status === 204) {
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ [API] Error:', { 
        name: error.name, 
        message: error.message, 
        endpoint, 
        url,
        stack: error.stack 
      });

      if (error?.name === 'TimeoutError' || error?.name === 'AbortError') {
        throw new Error('Délai d\'attente dépassé. Nouvelle tentative automatique…');
      }
      
      if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        // NOTE LOT1: message trompeur si saturation navigateur (boucle fetch) alors que l'API est up.
        // Toujours afficher l'origine API réellement configurée (jamais de host hardcodé)
        let backendHint = this.baseURL;
        try {
          if (this.baseURL.startsWith('/') && typeof window !== 'undefined') {
            backendHint = `${window.location.origin}${this.baseURL}`;
          } else if (!this.baseURL.startsWith('/')) {
            backendHint = new URL(this.baseURL).origin;
          }
        } catch {
          backendHint = this.baseURL;
        }
        const errorMsg = `Impossible de contacter le serveur backend. Vérifiez que le backend est démarré (${backendHint})`;
        console.error('❌ [API] Network error:', errorMsg);
        throw new Error(errorMsg);
      }
      throw error;
    }
  }

  async authenticate(path, payload) {
    const response = await fetch(`${this.baseURL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data?.error?.message || 'Erreur de connexion');
    }

    this.setTokens(data);
    if (data.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    }

    return data;
  }

  // Authentification unifiée (admin-web et app mobile)
  async login(email, password) {
    const data = await this.authenticate('/auth/login', { email, password });

    const userRole = data.user?.role;
    if (!userRole || !['SUPER_ADMIN', 'COMMERCANT', 'ADMIN'].includes(userRole)) {
      this.clearTokens();
      throw new Error('Accès non autorisé à l\'interface admin');
    }

    return data;
  }

  // Authentification admin (pour la page de login admin)
  async adminLogin(email, password) {
    const data = await this.authenticate('/admin-auth/login', {
      email: String(email || '').trim().toLowerCase(),
      password,
    });

    if (!data.success) {
      this.clearTokens();
      throw new Error('Erreur de connexion');
    }

    return data;
  }

  async verifyToken() {
    return this.request('/auth/profile');
  }

  async performLogout() {
    try {
      const refreshToken = this.getRefreshToken();
      await this.request('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      console.warn('Erreur lors de la déconnexion:', error);
    } finally {
      this.clearTokens();
      window.location.href = '/login';
    }
  }

  logout() {
    return this.performLogout();
  }

  logoutHard() {
    this.clearTokens();
    window.location.href = '/login';
  }

  getStoredUser() {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  // Commercants
  async getCommercants(params = {}) {
    try {
      // Construire l'URL avec les paramètres de requête
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/commercants?${queryString}` : '/commercants';
      return this.request(url);
    } catch (error) {
      console.error('🏪 [API] getCommercants() erreur:', error);
      throw error;
    }
  }

  async getCommercant(id) {
    return this.request(`/commercants/${id}`);
  }

  async getCommercantUser(commercantId) {
    return this.request(`/commercants/${commercantId}/user`);
  }

  async getCommercantsAccountsSummary() {
    return this.request('/commercants/accounts-summary');
  }

  async lookupCommercantUser(commercantId, email) {
    const params = new URLSearchParams({ email: email.trim().toLowerCase() });
    return this.request(`/commercants/${commercantId}/user/lookup?${params}`);
  }

  async createCommercantUser(commercantId, data) {
    return this.request(`/commercants/${commercantId}/user`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async linkCommercantUser(commercantId, userId) {
    return this.request(`/commercants/${commercantId}/user/link`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async getCommercantClients(commercantId) {
    return this.request(`/commercants/${commercantId}/clients`);
  }

  async lookupB2bClient(commercantId, email) {
    const q = encodeURIComponent(String(email || '').trim());
    return this.request(`/commercants/${commercantId}/clients/lookup?email=${q}`);
  }

  async addB2bClientPro(commercantId, userId) {
    return this.request(`/commercants/${commercantId}/clients/pro`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async patchB2bClientPro(commercantId, userId, isPro) {
    return this.request(`/commercants/${commercantId}/clients/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isPro: Boolean(isPro) }),
    });
  }

  async getClientProfessionalProfile(commercantId, userId) {
    return this.request(`/commercants/${commercantId}/clients/${userId}/professional-profile`);
  }

  async putClientProfessionalProfile(commercantId, userId, data) {
    return this.request(`/commercants/${commercantId}/clients/${userId}/professional-profile`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getClickAndCollectSettings(commercantId) {
    return this.request(`/commercants/${commercantId}/clickandcollect/settings`);
  }

  async updateClickAndCollectSettings(commercantId, data) {
    return this.request(`/commercants/${commercantId}/clickandcollect/settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createClickAndCollectCreneau(commercantId, data) {
    return this.request(`/commercants/${commercantId}/clickandcollect/creneaux`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createClickAndCollectCreneau(commercantId, data) {
    return this.request(`/commercants/${commercantId}/clickandcollect/creneaux`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClickAndCollectCreneau(commercantId, creneauId, data) {
    return this.request(`/commercants/${commercantId}/clickandcollect/creneaux/${creneauId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteClickAndCollectCreneau(commercantId, creneauId) {
    return this.request(`/commercants/${commercantId}/clickandcollect/creneaux/${creneauId}`, {
      method: 'DELETE',
    });
  }

  async syncClickAndCollectAuto(commercantId) {
    return this.request(`/commercants/${commercantId}/clickandcollect/sync`, {
      method: 'POST',
    });
  }

  async previewClickAndCollectAuto(commercantId) {
    return this.request(`/commercants/${commercantId}/clickandcollect/preview`);
  }

  async syncClickAndCollectAuto(commercantId) {
    return this.request(`/commercants/${commercantId}/clickandcollect/sync`, {
      method: 'POST',
    });
  }

  async previewClickAndCollectAuto(commercantId) {
    return this.request(`/commercants/${commercantId}/clickandcollect/sync/preview`, {
      method: 'POST',
    });
  }

  async createCommercant(data) {
    return this.request('/commercants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createCommercantWithUser(userData, commercantData, horaires = []) {
    return this.request('/commercants/create-with-user', {
      method: 'POST',
      body: JSON.stringify({ userData, commercantData, horaires }),
    });
  }

  async updateCommercant(id, data) {
    return this.request(`/commercants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * S4-C2a — Activer la politique de paiement par mode (matrice 4 booléens obligatoires).
   * Route exacte : POST .../politique-paiement-par-mode/activer
   */
  async activerPolitiquePaiementParMode(id, matrice) {
    return this.request(`/commercants/${id}/politique-paiement-par-mode/activer`, {
      method: 'POST',
      body: JSON.stringify(matrice),
    });
  }

  /**
   * Lot 4 — Éligibilité d’activation (lecture seule, avant interaction UI).
   * Route exacte : GET .../politique-paiement-par-mode/activation-eligibility
   */
  async getPolitiquePaiementActivationEligibility(id) {
    return this.request(
      `/commercants/${id}/politique-paiement-par-mode/activation-eligibility`,
    );
  }

  /**
   * S4-C2a — Désactiver (sans accent dans l’URL).
   * Route exacte : POST .../politique-paiement-par-mode/desactiver
   */
  async desactiverPolitiquePaiementParMode(id) {
    return this.request(`/commercants/${id}/politique-paiement-par-mode/desactiver`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  /**
   * S4-C2a — Remplacer la matrice si déjà active.
   * Route exacte : PUT .../politique-paiement-par-mode/matrice
   */
  async updateMatricePolitiquePaiementParMode(id, matrice) {
    return this.request(`/commercants/${id}/politique-paiement-par-mode/matrice`, {
      method: 'PUT',
      body: JSON.stringify(matrice),
    });
  }

  async testCommercantImprimante(id) {
    return this.request(`/commercants/${id}/imprimante/test`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async getCommercantImprimanteCockpit(id) {
    return this.request(`/commercants/${id}/imprimante`);
  }

  async getCommercantImprimanteJournal(id, limit = 50) {
    return this.request(`/commercants/${id}/imprimante/journal?limit=${limit}`);
  }

  async listMateriels(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/materiels${qs ? `?${qs}` : ''}`);
  }

  async getMateriel(id) {
    return this.request(`/materiels/${id}`);
  }

  async createMateriel(data) {
    return this.request('/materiels', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMateriel(id, data) {
    return this.request(`/materiels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async testMaterielPrint(id) {
    return this.request(`/materiels/${id}/test-print`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async getMaterielJournal(id, limit = 50) {
    return this.request(`/materiels/${id}/journal?limit=${limit}`);
  }

  async heartbeatMateriel(data) {
    return this.request('/materiels/heartbeat', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /** R3.2 — taxonomie commerce (familles & métiers) */
  async getCommerceTaxonomy(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/commerce-taxonomy${qs ? `?${qs}` : ''}`);
  }

  async getCommerceTaxonomyFamilies(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/commerce-taxonomy/families${qs ? `?${qs}` : ''}`);
  }

  async getCommerceTaxonomyMetiers(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/commerce-taxonomy/metiers${qs ? `?${qs}` : ''}`);
  }

  async getCommerceTaxonomyMetier(id) {
    return this.request(`/commerce-taxonomy/metiers/${id}`);
  }

  async createCommerceFamily(body) {
    return this.request('/commerce-taxonomy/families', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateCommerceFamily(id, body) {
    return this.request(`/commerce-taxonomy/families/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async deactivateCommerceFamily(id) {
    return this.request(`/commerce-taxonomy/families/${id}/deactivate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async reactivateCommerceFamily(id) {
    return this.request(`/commerce-taxonomy/families/${id}/reactivate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async archiveCommerceFamily(id) {
    return this.request(`/commerce-taxonomy/families/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async deleteCommerceFamily(id) {
    return this.request(`/commerce-taxonomy/families/${id}`, {
      method: 'DELETE',
    });
  }

  async createCommerceMetier(body) {
    return this.request('/commerce-taxonomy/metiers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateCommerceMetier(id, body) {
    return this.request(`/commerce-taxonomy/metiers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async deactivateCommerceMetier(id) {
    return this.request(`/commerce-taxonomy/metiers/${id}/deactivate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async reactivateCommerceMetier(id) {
    return this.request(`/commerce-taxonomy/metiers/${id}/reactivate`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async archiveCommerceMetier(id) {
    return this.request(`/commerce-taxonomy/metiers/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async deprecateCommerceMetier(id, body = {}) {
    return this.request(`/commerce-taxonomy/metiers/${id}/deprecate`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async deleteCommerceMetier(id) {
    return this.request(`/commerce-taxonomy/metiers/${id}`, {
      method: 'DELETE',
    });
  }

  /** G5 — qualité coords admin */
  async getCommercantCoordinateQuality(id) {
    return this.request(`/commercants/${id}/coordinates/quality`);
  }

  async validateCommercantCoordinates(id, data = {}) {
    return this.request(`/commercants/${id}/coordinates/validate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markCommercantCoordinatesReview(id) {
    return this.request(`/commercants/${id}/coordinates/mark-review`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async clearCommercantCoordinateOverride(id) {
    return this.request(`/commercants/${id}/coordinates/override`, {
      method: 'DELETE',
    });
  }

  async setCommercantCoordinateOverride(id, data) {
    return this.request(`/commercants/${id}/coordinates/override`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async applyCommercantGeocode(id, data) {
    return this.request(`/commercants/${id}/coordinates/apply-geocode`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async setCommercantCommerceADomicile(id, commerceADomicile) {
    return this.request(`/commercants/${id}/coordinates/home`, {
      method: 'PUT',
      body: JSON.stringify({ commerceADomicile: Boolean(commerceADomicile) }),
    });
  }

  async updateItinerantSlotCoordinates(commercantId, horaireId, data) {
    return this.request(`/commercants/${commercantId}/coordinates/horaires/${horaireId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async geocodeAdresse(data) {
    return this.request('/utils/geocode', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCommercant(id, password, confirmation) {
    return this.request(`/commercants/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ password, confirmation }),
    });
  }

  async getCommercantPermissions(id) {
    return this.request(`/commercants/${id}/permissions`);
  }

  async updateCommercantPermissions(id, permissions) {
    return this.request(`/commercants/${id}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    });
  }

  // Chat methods
  async updateCommercantChat(id, chatActif, chatDisponible = null) {
    const body = { chatActif: Boolean(chatActif) };
    if (chatDisponible !== null && chatDisponible !== undefined) {
      body.chatDisponible = Boolean(chatDisponible);
    }
    return this.request(`/commercants/${id}/chat`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async updateChatDisponibilite(commercantId, disponible) {
    return this.request(`/chat/commercant/${commercantId}/disponibilite`, {
      method: 'PUT',
      body: JSON.stringify({ disponible }),
    });
  }

  async getChatStatut(commercantId) {
    return this.request(`/chat/commercant/${commercantId}/statut`);
  }

  async getChatConversations(commercantId) {
    return this.request(`/chat/commercant/${commercantId}/conversations`);
  }

  async getChatMessages(commercantId, clientId) {
    return this.request(`/chat/commercant/${commercantId}/client/${clientId}`);
  }

  async sendChatMessage(commercantId, clientId, contenu) {
    return this.request(`/chat/commercant/${commercantId}/client/${clientId}`, {
      method: 'POST',
      body: JSON.stringify({ contenu }),
    });
  }

  async impersonateCommercant(id) {
    return this.request(`/commercants/${id}/impersonate`, {
      method: 'POST',
    });
  }

  /**
   * A1.2 — Démarre le mode assistance : POST impersonate + stockage sessionStorage.
   * Ne touche pas au JWT SA en localStorage.
   * @returns {{ context: object, expiresAt: string, commercant: object }}
   */
  async startAssistance(commercantId) {
    if (isAssistanceActive()) {
      throw new Error('Assistance déjà en cours dans cet onglet');
    }
    const response = await this.impersonateCommercant(commercantId);
    const context = startAssistanceSession(response);
    return {
      context,
      expiresAt: response.expiresAt,
      commercant: response.commercant,
      expiresInSeconds: response.expiresInSeconds,
    };
  }

  /**
   * A1.2 — Termine le mode assistance : journalisation backend (best-effort) + purge locale.
   * @returns {string|null} commercantId assisté (pour navigation post-sortie)
   */
  async endAssistance() {
    const session = peekAssistanceSession();
    const commercantId = session?.context?.commercantId || null;

    if (session?.token && commercantId) {
      try {
        // Appel avec le token assistance encore en place (getAccessToken le lit).
        await this.request(`/commercants/${commercantId}/impersonate/end`, {
          method: 'POST',
        }, false);
      } catch {
        // Best-effort : la purge locale prime toujours.
      }
    }

    clearAssistanceSession();
    return commercantId;
  }

  async getTableReservations(commercantId, params = {}) {
    const query = new URLSearchParams(params).toString();
    const suffix = query ? `?${query}` : '';
    return this.request(`/reservations/commercant/${commercantId}/list${suffix}`);
  }

  async getTableReservationsPendingCount(commercantId) {
    return this.request(`/reservations/commercant/${commercantId}/pending-count`);
  }

  async updateTableReservationStatut(id, statut, raison) {
    return this.request(`/reservations/${id}/statut`, {
      method: 'PATCH',
      body: JSON.stringify({ statut, raison }),
    });
  }

  async deleteUser(id, password, confirmation) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ password, confirmation }),
    });
  }

  async reactivateUser(id) {
    return this.request(`/users/${id}/reactivate`, {
      method: 'POST',
    });
  }

  // Zones de livraison
  async getZonesLivraison(commercantId, options = {}) {
    const { includeInactive = false } = options;
    const url = `/commercants/${commercantId}/zones-livraison${includeInactive ? '?includeInactive=true' : ''}`;
    return this.request(url);
  }

  async createZoneLivraison(commercantId, data) {
    return this.request(`/commercants/${commercantId}/zones-livraison`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateZoneLivraison(commercantId, zoneId, data) {
    return this.request(`/commercants/${commercantId}/zones-livraison/${zoneId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteZoneLivraison(commercantId, zoneId) {
    return this.request(`/commercants/${commercantId}/zones-livraison/${zoneId}`, {
      method: 'DELETE',
    });
  }

  async getLivraisonCreneaux(commercantId) {
    return this.request(`/commercants/${commercantId}/livraison/creneaux`);
  }

  async createLivraisonCreneau(commercantId, data) {
    return this.request(`/commercants/${commercantId}/livraison/creneaux`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLivraisonCreneau(commercantId, creneauId, data) {
    return this.request(`/commercants/${commercantId}/livraison/creneaux/${creneauId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLivraisonCreneau(commercantId, creneauId) {
    return this.request(`/commercants/${commercantId}/livraison/creneaux/${creneauId}`, {
      method: 'DELETE',
    });
  }

  // Produits
  async getProduits(params = '') {
    const search = params && typeof params === 'string' ? params : '';
    const endpoint = `/produits${search ? (search.startsWith('?') ? search : `?${search}`) : ''}`;
    return this.request(endpoint);
  }

  async getPrestations(params = '') {
    const search = params && typeof params === 'string' ? params : '';
    return this.request(`/prestations${search ? `?${search}` : ''}`);
  }

  async getProduitsByCommercant(commercantId, includeInactifs = true, pagination = null) {
    const params = new URLSearchParams();
    if (includeInactifs) params.append('includeInactifs', 'true');
    
    // Pagination optionnelle (page, pageSize)
    if (pagination) {
      if (pagination.page) params.append('page', pagination.page);
      if (pagination.pageSize) params.append('pageSize', pagination.pageSize);
    }
    
    const queryString = params.toString();
    const url = `/produits/commercant/${commercantId}${queryString ? `?${queryString}` : ''}`;
    
    return this.request(url);
  }

  async getProduit(id) {
    return this.request(`/produits/${id}`);
  }

  async getCatalogueRubriques(commercantId, includeInactives = false) {
    const qs = includeInactives ? '?includeInactives=true' : '';
    return this.request(`/catalogue-rubriques/commercant/${commercantId}${qs}`);
  }

  async createCatalogueRubrique(commercantId, data) {
    return this.request(`/catalogue-rubriques/commercant/${commercantId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async patchCatalogueRubrique(rubriqueId, data) {
    return this.request(`/catalogue-rubriques/${rubriqueId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async moveCatalogueRubrique(commercantId, rubriqueId, direction) {
    return this.request(
      `/catalogue-rubriques/commercant/${commercantId}/${rubriqueId}/move`,
      {
        method: 'POST',
        body: JSON.stringify({ direction }),
      }
    );
  }

  async reorderCatalogueRubriques(commercantId, { parentId = null, orderedIds }) {
    return this.request(`/catalogue-rubriques/commercant/${commercantId}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ parentId, orderedIds }),
    });
  }

  async reorderProduitsInRubrique(commercantId, { categorie, sousCategorie = null, orderedIds }) {
    return this.request(`/catalogue-rubriques/commercant/${commercantId}/reorder-produits`, {
      method: 'POST',
      body: JSON.stringify({ categorie, sousCategorie, orderedIds }),
    });
  }

  async deleteCatalogueRubrique(commercantId, rubriqueId) {
    return this.request(`/catalogue-rubriques/commercant/${commercantId}/${rubriqueId}`, {
      method: 'DELETE',
    });
  }

  async getProduitOptions(id) {
    return this.request(`/produits/${id}/options?_=${Date.now()}`, {
      headers: { 'X-Caisse-POS': '1' },
    });
  }

  // Prestations & rendez-vous (services)
  async getPrestationsByCommercant(commercantId, type = '', extraParams = {}) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });
    const qs = params.toString();
    return this.request(`/prestations/commercant/${commercantId}${qs ? `?${qs}` : ''}`);
  }

  async getPrestationById(prestationId) {
    // Récupérer une prestation spécifique par son ID (peu importe son type)
    return this.request(`/prestations/${prestationId}`);
  }

  async createPrestation(data) {
    return this.request('/prestations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePrestation(id, data) {
    return this.request(`/prestations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePrestation(id) {
    return this.request(`/prestations/${id}`, {
      method: 'DELETE',
    });
  }

  async getRendezVousCreneaux(commercantId, params = {}) {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      )
    ).toString();
    return this.request(`/rendez-vous/creneaux/${commercantId}${qs ? `?${qs}` : ''}`);
  }

  async getPrestationCreneaux(prestationId) {
    return this.request(`/prestations/${prestationId}/creneaux`);
  }

  async createPrestationCreneau(prestationId, data) {
    return this.request(`/prestations/${prestationId}/creneaux`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePrestationCreneau(prestationId, creneauId, data) {
    return this.request(`/prestations/${prestationId}/creneaux/${creneauId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePrestationCreneau(prestationId, creneauId) {
    return this.request(`/prestations/${prestationId}/creneaux/${creneauId}`, {
      method: 'DELETE',
    });
  }

  async getPrestationOptions(prestationId) {
    return this.request(`/prestations/${prestationId}/options`);
  }

  async createPrestationOption(prestationId, data) {
    return this.request(`/prestations/${prestationId}/options`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePrestationOption(optionId, data) {
    return this.request(`/prestations/options/${optionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePrestationOption(optionId) {
    return this.request(`/prestations/options/${optionId}`, {
      method: 'DELETE',
    });
  }

  async getRessourcesByCommercant(commercantId, params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/ressources/commercant/${commercantId}${qs ? `?${qs}` : ''}`);
  }

  async createRessource(commercantId, data) {
    return this.request(`/ressources/commercant/${commercantId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRessource(id, data) {
    return this.request(`/ressources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRessource(id) {
    return this.request(`/ressources/${id}`, { method: 'DELETE' });
  }

  async getRendezVousByCommercant(commercantId, params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {}),
    ).toString();

    return this.request(
      `/rendez-vous/commercant/${commercantId}${queryString ? `?${queryString}` : ''}`,
    );
  }

  /** Récupère tous les RDV d'une plage (pagination complète — planning semaine). */
  async getRendezVousByCommercantAll(commercantId, params = {}) {
    const pageSize = 100;
    let page = 1;
    let all = [];
    let hasNext = true;

    while (hasNext) {
      const response = await this.getRendezVousByCommercant(commercantId, {
        ...params,
        page,
        pageSize,
      });
      const batch = response?.data || response?.rendezVous || [];
      all = all.concat(batch);
      hasNext = Boolean(response?.pagination?.hasNext);
      page += 1;
      if (page > 100) break;
    }

    return all;
  }

  async updateRendezVous(id, data) {
    return this.request(`/rendez-vous/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async assignRendezVousInstructeur(id, instructeurId) {
    return this.request(`/rendez-vous/${id}/instructeur`, {
      method: 'PUT',
      body: JSON.stringify({ instructeurId }),
    });
  }

  async getRendezVousAssignableInstructeurs(id) {
    return this.request(`/rendez-vous/${id}/instructeurs-assignables`);
  }

  /** Slice 2.2 — Terminer RDV (+ encaisser solde optionnel) */
  async terminerRendezVous(id, body = {}, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(body),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request(`/rendez-vous/${id}/terminer`, options);
  }

  async cancelRendezVous(id, data = {}) {
    return this.request(`/rendez-vous/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  async createProduit(data, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(data),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request('/produits', options);
  }

  async updateProduit(id, data) {
    return this.request(`/produits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async generateProduitInternalBarcode(produitId) {
    return this.request(`/produits/${produitId}/internal-barcode`, { method: 'POST' });
  }

  async generateChoixInternalBarcode(choixOptionId) {
    return this.request(`/produits/choix/${choixOptionId}/internal-barcode`, { method: 'POST' });
  }

  async deleteProduit(id) {
    return this.request(`/produits/${id}`, {
      method: 'DELETE',
    });
  }

  // --- Caractéristiques produit (mémoire métier par commerçant — M2) ---

  async listCommercantCaracteristiques({ commercantId, q = '', limit = 8 } = {}) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    if (q) params.set('q', q);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString();
    return this.request(`/commercant/caracteristiques${qs ? `?${qs}` : ''}`);
  }

  async addProduitCaracteristique(produitId, body) {
    return this.request(`/produits/${produitId}/caracteristiques`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async patchProduitCaracteristique(produitId, caracteristiqueId, body) {
    return this.request(`/produits/${produitId}/caracteristiques/${caracteristiqueId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async deleteProduitCaracteristique(produitId, caracteristiqueId) {
    return this.request(`/produits/${produitId}/caracteristiques/${caracteristiqueId}`, {
      method: 'DELETE',
    });
  }

  // --- Prix Canal ---

  async getPrixCanal(produitId) {
    return this.request(`/produits/${produitId}/prix-canal`);
  }

  async upsertPrixCanal(produitId, data) {
    return this.request(`/produits/${produitId}/prix-canal`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deletePrixCanal(produitId, prixCanalId) {
    return this.request(`/produits/${produitId}/prix-canal/${prixCanalId}`, {
      method: 'DELETE',
    });
  }

  // Gestion intelligente des stocks
  async desactiverProduitsRupture(commercantId) {
    return this.request('/stock-management/desactiver-produits-rupture', {
      method: 'POST',
      body: JSON.stringify({ commercantId }),
    });
  }

  async reactiverProduitsDisponibles(commercantId) {
    return this.request('/stock-management/reactiver-produits-disponibles', {
      method: 'POST',
      body: JSON.stringify({ commercantId }),
    });
  }

  async getAlertesStockBas(commercantId) {
    return this.request(`/stock-management/alertes-stock-bas?commercantId=${commercantId}`);
  }

  async miseAJourStock(stockId, nouveauStock, nouveauDisponible, commercantId) {
    return this.request('/stock-management/mise-a-jour-stock', {
      method: 'POST',
      body: JSON.stringify({
        stockId,
        nouveauStock,
        nouveauDisponible,
        commercantId,
      }),
    });
  }

  // Commandes
  async getCommandes(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/commandes/admin${queryString ? `?${queryString}` : ''}`);
  }

  async getCommande(id, params = {}) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    ).toString();
    const suffix = queryString ? `?${queryString}` : '';
    return this.request(`/commandes/${id}${suffix}`);
  }

  // Gestion des fermetures temporaires
  async getFermeturesTemporaires(commercantId) {
    return this.request(`/fermetures-temporaires/${commercantId}`);
  }

  async updateFermeturesTemporaires(commercantId, fermetures) {
    return this.request(`/fermetures-temporaires/${commercantId}`, {
      method: 'POST',
      body: JSON.stringify({ fermetures }),
    });
  }

  async deleteFermetureTemporaire(id) {
    return this.request(`/fermetures-temporaires/${id}`, {
      method: 'DELETE',
    });
  }

  async checkFermetureTemporaire(commercantId) {
    return this.request(`/fermetures-temporaires/check/${commercantId}`);
  }

  async updateCommande(id, data) {
    return this.request(`/commandes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async reprintTicket(commandeId) {
    return this.request(`/commandes/${commandeId}/reprint-ticket`, {
      method: 'POST',
    });
  }

  // Utilisateurs
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/users${queryString ? `?${queryString}` : ''}`);
  }

  async getUser(id) {
    return this.request(`/users/${id}`);
  }

  async createUser(data) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async adminForgotPassword(email) {
    return publicJsonPost(this.baseURL, '/admin-auth/forgot-password', { email });
  }

  /** Invitation nouveau collaborateur (template Bienvenue, purpose=admin_invite). */
  async adminInviteCollaborator(email) {
    return publicJsonPost(this.baseURL, '/admin-auth/invite', { email });
  }

  async getMyPlatformAccess() {
    return this.request('/admin/nr2j/me/access');
  }

  async getNr2jCollaboratorMeta() {
    return this.request('/admin/nr2j/collaborators/meta');
  }

  async listNr2jCollaborators() {
    return this.request('/admin/nr2j/collaborators');
  }

  async listNr2jTeam() {
    return this.request('/admin/nr2j/team');
  }

  async listNr2jMyTeam() {
    return this.request('/admin/nr2j/my-team');
  }

  async getNr2jMyTeamMember(userId) {
    return this.request(`/admin/nr2j/my-team/${encodeURIComponent(userId)}`);
  }

  async getNr2jMyTeamMemberSection(userId, section) {
    return this.request(
      `/admin/nr2j/my-team/${encodeURIComponent(userId)}/${encodeURIComponent(section)}`,
    );
  }

  async createNr2jCollaborator(data) {
    return this.request('/admin/nr2j/collaborators', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNr2jCollaborator(id, data) {
    return this.request(`/admin/nr2j/collaborators/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async listNr2jApprovals() {
    return this.request('/admin/nr2j/approvals');
  }

  async getNr2jApprovalsPendingCount() {
    return this.request('/admin/nr2j/approvals/pending-count');
  }

  async decideNr2jApproval(id, data) {
    return this.request(`/admin/nr2j/approvals/${id}/decide`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async submitNr2jQuote(id) {
    return this.request(`/admin/nr2j/quotes/${id}/submit`, { method: 'POST', body: '{}' });
  }

  async listNr2jActivity(params = {}) {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null && value !== ''),
    ).toString();
    return this.request(`/admin/nr2j/activity${query ? `?${query}` : ''}`);
  }

  async adminResetPassword(token, password) {
    return publicJsonPost(this.baseURL, '/admin-auth/complete-password-reset', {
      token,
      password,
    });
  }

  // Réinitialiser le mot de passe d'un utilisateur (SUPER_ADMIN uniquement)
  async resetUserPassword(userId, newPassword, generatePassword = false) {
    return this.request('/admin-auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ userId, newPassword, generatePassword }),
    });
  }

  // Promotions
  async getPromotions() {
    return this.request('/promotions');
  }

  async getPromotion(id) {
    return this.request(`/promotions/${id}`);
  }

  async createPromotion(data) {
    return this.request('/promotions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePromotion(id, data) {
    return this.request(`/promotions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePromotion(id) {
    return this.request(`/promotions/${id}`, {
      method: 'DELETE',
    });
  }

  // Promotions clients
  async getClientPromotions(userId, commercantId = null) {
    const url = commercantId 
      ? `/promotions-clients/user/${userId}?commercantId=${commercantId}`
      : `/promotions-clients/user/${userId}`;
    return this.request(url);
  }

  async getClientPromotion(id) {
    return this.request(`/promotions-clients/${id}`);
  }

  async createClientPromotion(data) {
    return this.request('/promotions-clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClientPromotion(id, data) {
    return this.request(`/promotions-clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteClientPromotion(id) {
    return this.request(`/promotions-clients/${id}`, {
      method: 'DELETE',
    });
  }

  async sendClientRelationEmail(userId, { subject, message }) {
    return this.request(`/clients/${userId}/email`, {
      method: 'POST',
      body: JSON.stringify({ subject, message }),
    });
  }

  // Analytics
  async getAnalytics({ period, dateDebut, dateFin, commercantId } = {}) {
    const params = new URLSearchParams();
    if (dateDebut && dateFin) {
      params.set('dateDebut', dateDebut);
      params.set('dateFin', dateFin);
    } else if (period && period !== 'custom') {
      params.set('period', period.replace('d', 'j'));
    }
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(`/analytics/dashboard${qs ? `?${qs}` : ''}`);
  }

  async getHealthStatus() {
    return this.request('/health');
  }

  async getCommercantAnalyticsOverview(id, { period, dateDebut, dateFin, timezone } = {}) {
    const params = new URLSearchParams();
    if (period) params.set('period', period);
    if (dateDebut) params.set('dateDebut', dateDebut);
    if (dateFin) params.set('dateFin', dateFin);
    if (timezone) params.set('timezone', timezone);
    const qs = params.toString();
    return this.request(`/analytics/commercant/${id}/overview${qs ? `?${qs}` : ''}`);
  }

  async downloadCommercantComportementCsv(id, { period, dateDebut, dateFin, timezone } = {}) {
    const params = new URLSearchParams();
    if (period) params.set('period', period);
    if (dateDebut) params.set('dateDebut', dateDebut);
    if (dateFin) params.set('dateFin', dateFin);
    if (timezone) params.set('timezone', timezone);
    const qs = params.toString();
    const token = this.getAccessToken();
    const response = await fetch(
      `${this.baseURL}/analytics/commercant/${id}/comportement-export${qs ? `?${qs}` : ''}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!response.ok) {
      throw new Error('Export CSV impossible');
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] || `comportement-site-${period || 'export'}.csv`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getClientAnalytics(clientId, commercantId = null) {
    let url = `/clients/${clientId}/analytics`;
    if (commercantId) {
      url += `?commercantId=${commercantId}`;
    }
    return this.request(url);
  }

  async getClientComportement(clientId, { commercantId, period, dateDebut, dateFin, timezone } = {}) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    if (period) params.set('period', period);
    if (dateDebut) params.set('dateDebut', dateDebut);
    if (dateFin) params.set('dateFin', dateFin);
    if (timezone) params.set('timezone', timezone);
    const qs = params.toString();
    return this.request(`/clients/${clientId}/comportement${qs ? `?${qs}` : ''}`);
  }

  async getPageViewsStats({ period = '30d', commercantId = null, dateDebut, dateFin } = {}) {
    const params = new URLSearchParams();
    if (dateDebut && dateFin) {
      params.set('dateDebut', dateDebut);
      params.set('dateFin', dateFin);
    } else {
      params.set('period', String(period).replace('j', 'd'));
    }
    if (commercantId) {
      params.set('commercantId', commercantId);
    }
    return this.request(`/page-views/stats?${params.toString()}`);
  }

  // Communication
  async getNotifications() {
    return this.request('/notifications');
  }

  async createNotification(data) {
    return this.request('/notifications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteNotification(id) {
    return this.request(`/notifications/${id}`, {
      method: 'DELETE',
    });
  }

  async updateNotificationAttribution(id, commercantId) {
    return this.request(`/notifications/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ commercantId }),
    });
  }

  // Growth Communication (historique + campagnes email)
  async getGrowthEntitlements(commercantId = null) {
    const q = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/growth/entitlements${q}`);
  }

  async patchGrowthTier({ commercantId, growthTier }) {
    return this.request('/growth/tier', {
      method: 'PATCH',
      body: JSON.stringify({ commercantId, growthTier }),
    });
  }

  async getGrowthBonuses(commercantId) {
    const q = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/growth/bonuses${q}`);
  }

  async createGrowthBonus(data) {
    return this.request('/growth/bonuses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeGrowthBonus(bonusId, revokeReason = null) {
    return this.request(`/growth/bonuses/${bonusId}/revoke`, {
      method: 'POST',
      body: JSON.stringify(revokeReason ? { revokeReason } : {}),
    });
  }

  async getGrowthStats(commercantId = null, periodYm = null) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    if (periodYm) params.set('periodYm', periodYm);
    const q = params.toString();
    return this.request(`/growth/stats${q ? `?${q}` : ''}`);
  }

  async getGrowthCampaigns({ commercantId, status, canal, q, page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    if (status) params.set('status', status);
    if (canal) params.set('canal', canal);
    if (q) params.set('q', q);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    return this.request(`/growth/campaigns?${params.toString()}`);
  }

  async getGrowthCampaign(id, commercantId = null) {
    const q = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/growth/campaigns/${id}${q}`);
  }

  async createGrowthCampaign(data) {
    return this.request('/growth/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendGrowthCampaignTest(data) {
    return this.request('/growth/campaigns/test', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendGrowthCampaign(id, data = {}) {
    const { sendIdempotencyKey, ...body } = data || {};
    const options = {
      method: 'POST',
      body: JSON.stringify({
        ...body,
        ...(sendIdempotencyKey ? { sendIdempotencyKey } : {}),
      }),
    };
    if (sendIdempotencyKey) {
      options.headers = { 'Idempotency-Key': sendIdempotencyKey };
    }
    return this.request(`/growth/campaigns/${id}/send`, options);
  }

  async patchGrowthCampaignInAppDisplay(id, data = {}) {
    return this.request(`/growth/campaigns/${id}/in-app-display`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Settings
  async getSettings() {
    return this.request('/settings');
  }

  async updateSettings(data) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getSettingsHealth() {
    return this.request('/settings/health');
  }

  // Livreurs
  async getLivreurs(commercantId = null) {
    const url = commercantId ? `/livreurs/commercant/${commercantId}` : '/livreurs';
    return this.request(url);
  }

  async getLivreur(id) {
    return this.request(`/livreurs/${id}`);
  }

  async createLivreur(data) {
    return this.request('/livreurs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLivreur(id, data) {
    return this.request(`/livreurs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLivreur(id, password, confirmation) {
    return this.request(`/livreurs/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ password, confirmation }),
    });
  }

  async getLivreurStats(id) {
    return this.request(`/livreurs/${id}/stats`);
  }

  async renvoyerActivationLivreur(id) {
    return this.request(`/livreurs/${id}/renvoyer-activation`, {
      method: 'POST',
    });
  }

  async setLivreurActif(id, actif) {
    return this.request(`/livreurs/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ actif }),
    });
  }

  async revoquerSessionsLivreur(id) {
    return this.request(`/livreurs/${id}/revoquer-sessions`, {
      method: 'POST',
    });
  }

  async libererLivreur(id) {
    return this.request(`/livreurs/${id}/liberer`, {
      method: 'POST',
    });
  }

  async reassignerCommandeLivreur(id, newLivreurId) {
    return this.request(`/livreurs/${id}/reassigner-commande`, {
      method: 'POST',
      body: JSON.stringify({ newLivreurId }),
    });
  }

  async assignerLivreur(commandeId, { livreurId, force = false } = {}) {
    return this.request(`/commandes/${commandeId}/assigner-livreur`, {
      method: 'POST',
      body: JSON.stringify({ livreurId, force }),
    });
  }

  // Dashboard
  async getDashboardStats({ period = '30j' } = {}) {
    try {
      const analytics = await this.getAnalytics({ period });
      return {
        totalCommercants: analytics.totalCommercants,
        commercantsActifs: analytics.commercantsActifs ?? analytics.totalCommercants,
        totalProduits: analytics.totalProduits,
        totalPrestations: analytics.totalPrestations,
        totalCommandes: analytics.totalCommandes,
        totalUsers: analytics.totalUsers,
        totalRevenus: analytics.totalRevenus,
        panierMoyen: analytics.panierMoyen,
        periodLabel: analytics.periodLabel,
        totalVuesGlobales: analytics.totalVuesGlobales || 0,
        totalVuesCommercants: analytics.totalVuesCommercants || 0,
        totalVuesTotal: analytics.totalVuesTotal || 0,
        commandesRecentes: analytics.commandesRecentes,
        commandesParStatut: analytics.commandesParStatut,
        commandesParCanal: analytics.commandesParCanal || [],
        commandesParPaiement: analytics.commandesParPaiement || [],
        topProduits: analytics.topProduits,
        evolutionCommandes: analytics.evolutionCommandes,
        dateDebut: analytics.dateDebut,
        dateFin: analytics.dateFin,
        systemHealth: analytics.systemHealth || { apiStatus: 'ok', avgLatency: 0, errorRate: 0 }
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        totalCommercants: 0,
        totalProduits: 0,
        totalPrestations: 0,
        totalCommandes: 0,
        totalUsers: 0,
        totalRevenus: 0,
        totalVuesGlobales: 0,
        totalVuesCommercants: 0,
        commandesRecentes: [],
        commandesParStatut: [],
        commandesParCanal: [],
        commandesParPaiement: [],
        topProduits: [],
        evolutionCommandes: [],
        systemHealth: { apiStatus: 'error', avgLatency: 0, errorRate: 0 }
      };
    }
  }

  // Types de commerce
  async getTypesCommerce() {
    return this.request('/types-commerce');
  }

  async getTypeCommerce(id) {
    return this.request(`/types-commerce/${id}`);
  }

  async getTypeCommerceByCode(code) {
    return this.request(`/types-commerce/code/${code}`);
  }

  // Variantes
  async getVariantes(produitId) {
    return this.request(`/variantes/produit/${produitId}`);
  }

  async createVariante(data) {
    return this.request('/variantes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createVariantesBulk(produitId, variantes) {
    return this.request('/variantes/bulk', {
      method: 'POST',
      body: JSON.stringify({ produitId, variantes }),
    });
  }

  async updateVariante(id, data) {
    return this.request(`/variantes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVariante(id) {
    return this.request(`/variantes/${id}`, {
      method: 'DELETE',
    });
  }

  async getTemplatesVariantes(typeCommerce = null) {
    const query = typeCommerce ? `?typeCommerce=${typeCommerce}` : '';
    return this.request(`/variantes/templates${query}`);
  }

  // Imports IA menus
  async createMenuImportJob(payload) {
    return this.request('/menu-imports', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async uploadMenuImportFile(formData) {
    return this.request('/menu-imports/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async getMenuImportJob(id) {
    return this.request(`/menu-imports/${id}`);
  }

  async getMenuImportPreview(id) {
    return this.request(`/menu-imports/${id}/preview`);
  }

  async updateMenuImportDraft(id, draftJson) {
    return this.request(`/menu-imports/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ draftJson }),
    });
  }

  async deleteMenuImportJob(id) {
    return this.request(`/menu-imports/${id}`, {
      method: 'DELETE',
    });
  }

  async commitMenuImportJob(id, mergeStrategy = 'MERGE_PREFERRED', decisions = undefined) {
    // IA-1 : decisions = [{ productName, decision: 'IGNORE'|'CREATE_NEW'|'REPLACE' }]
    // Sans décision, le backend applique IGNORE sur toute collision (aucun écrasement).
    const payload = { mergeStrategy };
    if (Array.isArray(decisions) && decisions.length) {
      payload.decisions = decisions;
    }
    return this.request(`/menu-imports/${id}/commit`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // F2.2a — Catalogue Définitions (GET lecture seule)
  async getFideliteRecompenses(commercantId, query = {}) {
    const params = new URLSearchParams();
    if (query.statut) params.set('statut', query.statut);
    if (query.limit != null) params.set('limit', String(query.limit));
    if (query.offset != null) params.set('offset', String(query.offset));
    const qs = params.toString();
    const path = `/fidelite/recompenses/${commercantId}${qs ? `?${qs}` : ''}`;
    return this.request(path, { method: 'GET' });
  }

  // Lot 4B — Cartes cadeaux (templates Admin)
  async listGiftCardTemplates(commercantId, query = {}) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    if (query.includeInactifs != null) {
      params.set('includeInactifs', String(query.includeInactifs));
    }
    const qs = params.toString();
    return this.request(`/gift-card-templates${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async getGiftCardTemplate(id, commercantId) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(`/gift-card-templates/${id}${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  }

  async createGiftCardTemplate(body) {
    return this.request('/gift-card-templates', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateGiftCardTemplate(id, body) {
    return this.request(`/gift-card-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async setGiftCardTemplateStatus(id, actif) {
    return this.request(`/gift-card-templates/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ actif: Boolean(actif) }),
    });
  }

  // V1 Expédition — settings & profils colis (moduleExpedition requis)
  async getExpeditionSettings(commercantId) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(`/expedition/settings${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async getExpeditionCapabilities(commercantId) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(`/expedition/capabilities${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async handoffExpeditionShipment(shipmentId, commercantId) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(
      `/expedition/shipments/${encodeURIComponent(shipmentId)}/handoff${qs ? `?${qs}` : ''}`,
      { method: 'POST' },
    );
  }

  async createBoxtalShippingOrder(shipmentId, commercantId) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(
      `/expedition/shipments/${encodeURIComponent(shipmentId)}/shipping-order${qs ? `?${qs}` : ''}`,
      { method: 'POST' },
    );
  }

  async getExpeditionShippingLabelStatus(shipmentId, commercantId) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(
      `/expedition/shipments/${encodeURIComponent(shipmentId)}/shipping-label${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
    );
  }

  async syncExpeditionShipmentTracking(shipmentId, commercantId) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(
      `/expedition/shipments/${encodeURIComponent(shipmentId)}/tracking/sync${qs ? `?${qs}` : ''}`,
      { method: 'POST' },
    );
  }

  async downloadExpeditionShippingLabel(shipmentId, commercantId) {
    const token = this.getAccessToken();
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    const response = await fetch(
      `${this.baseURL}/expedition/shipments/${encodeURIComponent(shipmentId)}/shipping-label/download${qs ? `?${qs}` : ''}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    if (!response.ok) {
      let message = 'Téléchargement de l’étiquette impossible';
      try {
        const err = await response.json();
        if (err?.detail || err?.message) message = err.detail || err.message;
        if (err?.code === 'LABEL_PENDING') {
          message = 'Étiquette en cours de génération';
        }
      } catch {
        /* ignore */
      }
      const error = new Error(message);
      error.code = response.status === 409 ? 'LABEL_PENDING' : undefined;
      throw error;
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const labelFormat = response.headers.get('x-label-format') || '';
    const filename = match?.[1] || `etiquette-${shipmentId.slice(0, 8)}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return { filename, format: labelFormat };
  }

  async updateExpeditionSettings(body) {
    return this.request('/expedition/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async listExpeditionProfiles(commercantId, query = {}) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    if (query.includeInactive != null) {
      params.set('includeInactive', String(query.includeInactive));
    }
    const qs = params.toString();
    return this.request(`/expedition/profiles${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async createExpeditionProfile(body) {
    return this.request('/expedition/profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // B2.1b — solution transport Expédition (Super Admin uniquement)
  async getShippingIntegration(commercantId) {
    return this.request(`/commercants/${commercantId}/shipping-integration`, {
      method: 'GET',
    });
  }

  async putShippingIntegration(commercantId, body) {
    return this.request(`/commercants/${commercantId}/shipping-integration`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  // Boxtal B2.1 — credentials provider (Super Admin uniquement)
  async getBoxtalShippingProviderStatus(commercantId) {
    return this.request(`/commercants/${commercantId}/shipping-provider/boxtal/status`, {
      method: 'GET',
    });
  }

  async putBoxtalShippingProviderCredentials(commercantId, body) {
    return this.request(`/commercants/${commercantId}/shipping-provider/boxtal/credentials`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async testBoxtalShippingProviderConnection(commercantId) {
    return this.request(`/commercants/${commercantId}/shipping-provider/boxtal/test-connection`, {
      method: 'POST',
    });
  }

  async deleteBoxtalShippingProviderCredentials(commercantId) {
    return this.request(`/commercants/${commercantId}/shipping-provider/boxtal/credentials`, {
      method: 'DELETE',
    });
  }

  async updateExpeditionProfile(id, commercantId, body) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    const payload = { ...(body || {}) };
    delete payload.commercantId;
    return this.request(`/expedition/profiles/${id}${qs ? `?${qs}` : ''}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async setExpeditionProfileStatus(id, commercantId, isActive) {
    const params = new URLSearchParams();
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(`/expedition/profiles/${id}/status${qs ? `?${qs}` : ''}`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive: Boolean(isActive) }),
    });
  }

  async getFideliteRecompense(commercantId, id) {
    return this.request(`/fidelite/recompenses/${commercantId}/${id}`, {
      method: 'GET',
    });
  }

  // F2.2b — Mutations catalogue (API F2.1)
  async createFideliteRecompense(commercantId, body) {
    return this.request(`/fidelite/recompenses/${commercantId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateFideliteRecompense(commercantId, id, body) {
    return this.request(`/fidelite/recompenses/${commercantId}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async archiverFideliteRecompense(commercantId, id) {
    return this.request(`/fidelite/recompenses/${commercantId}/${id}/archiver`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async reorderFideliteRecompenses(commercantId, items) {
    return this.request(`/fidelite/recompenses/${commercantId}/ordre`, {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    });
  }

  // Fidélité - Scanner QR Code
  async scanQRCode(qrCodeToken) {
    return this.request(`/fidelite/scan/${qrCodeToken}`, {
      method: 'GET',
    });
  }

  // Fidélité - Créditer manuellement
  async crediterPointsManuel(userId, montantTotal, description) {
    return this.request('/fidelite/crediter-manuel', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        montantTotal,
        description,
      }),
    });
  }

  // Fidélité - Ajuster les points d'un client (service client)
  async ajusterPointsClient(userId, points, raison) {
    return this.request('/fidelite/ajuster-points', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        points,
        raison,
      }),
    });
  }

  // Niveaux de carte de fidélité - Liste
  async getNiveauxCarteFidelite() {
    return this.request('/niveaux-carte-fidelite');
  }

  // Niveaux de carte de fidélité - Mettre à jour
  async updateNiveauCarteFidelite(id, data) {
    return this.request(`/niveaux-carte-fidelite/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Avantages Clickorico globaux - Liste
  async getAvantagesClickorico(actif = null, statutMinimum = null) {
    const params = new URLSearchParams();
    if (actif !== null) params.append('actif', actif);
    if (statutMinimum) params.append('statutMinimum', statutMinimum);
    const query = params.toString();
    return this.request(`/avantages-clickorico${query ? `?${query}` : ''}`);
  }

  // Avantages Clickorico globaux - Créer
  async createAvantageClickorico(data) {
    return this.request('/avantages-clickorico', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Avantages Clickorico globaux - Modifier
  async updateAvantageClickorico(id, data) {
    return this.request(`/avantages-clickorico/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Avantages Clickorico globaux - Supprimer
  async deleteAvantageClickorico(id) {
    return this.request(`/avantages-clickorico/${id}`, {
      method: 'DELETE',
    });
  }

  // Recherche de clients (pour service client)
  async searchUsers(query) {
    return this.request(`/users?search=${encodeURIComponent(query)}&pageSize=20`);
  }

  // —— Module Caisse (POS) ——
  _caisseQuery(commercantId) {
    return commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
  }

  async getCaisseSession(commercantId = null) {
    return this.request(`/caisse/session/active${this._caisseQuery(commercantId)}`);
  }

  async getCaisseOperationalHealth(commercantId = null) {
    return this.request(`/caisse/operational-health${this._caisseQuery(commercantId)}`);
  }

  async getCaisseSessionCloseOperationalPreview(commercantId = null) {
    return this.request(
      `/caisse/session/close-operational-preview${this._caisseQuery(commercantId)}`
    );
  }

  async getCaissePostes(commercantId = null) {
    return this.request(`/caisse/postes${this._caisseQuery(commercantId)}`);
  }

  async getCaissePostesAdmin(commercantId = null) {
    return this.request(`/caisse/postes/admin${this._caisseQuery(commercantId)}`);
  }

  async getCaissePosteEnrollments(commercantId, posteId) {
    const q = this._caisseQuery(commercantId);
    const active = q ? `${q}&activeOnly=true` : '?activeOnly=true';
    return this.request(`/caisse/postes/${posteId}/enrollments${active}`);
  }

  async createCaissePoste(commercantId, body) {
    return this.request('/caisse/postes', {
      method: 'POST',
      body: JSON.stringify({ ...body, ...(commercantId ? { commercantId } : {}) }),
    });
  }

  async updateCaissePoste(commercantId, posteId, body) {
    return this.request(`/caisse/postes/${posteId}`, {
      method: 'PATCH',
      body: JSON.stringify({ ...body, ...(commercantId ? { commercantId } : {}) }),
    });
  }

  async deleteCaissePoste(commercantId, posteId) {
    return this.request(`/caisse/postes/${posteId}`, {
      method: 'DELETE',
      body: JSON.stringify(commercantId ? { commercantId } : {}),
    });
  }

  async enrollCaissePosteDevice(commercantId, posteId, deviceId) {
    return this.request(`/caisse/postes/${posteId}/enroll-device`, {
      method: 'POST',
      body: JSON.stringify({
        deviceId,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async revokeCaissePosteDevice(commercantId, deviceId) {
    return this.request(`/caisse/postes/devices/${deviceId}/revoke`, {
      method: 'POST',
      body: JSON.stringify(commercantId ? { commercantId } : {}),
    });
  }

  async ouvrirCaisseSession(fondDeCaisse = 0, commercantId = null) {
    return this.request('/caisse/session/ouvrir', {
      method: 'POST',
      body: JSON.stringify({ fondDeCaisse, ...(commercantId ? { commercantId } : {}) }),
    });
  }

  async getCaisseTresorerieHistorique(commercantId = null, sessionCaisseId = null) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (sessionCaisseId) q.set('sessionCaisseId', sessionCaisseId);
    const qs = q.toString();
    return this.request(`/caisse/tresorerie/historique${qs ? `?${qs}` : ''}`);
  }

  async postCaisseTresorerieMouvement(body, commercantId = null, idempotencyKey = null) {
    return this.request('/caisse/tresorerie/mouvement', {
      method: 'POST',
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
      body: JSON.stringify({
        ...body,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async postCaisseTresorerieContrepasser(mouvementId, body, commercantId = null, idempotencyKey = null) {
    return this.request(`/caisse/tresorerie/mouvement/${mouvementId}/contrepasser`, {
      method: 'POST',
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
      body: JSON.stringify({
        ...body,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async fermerCaisseSession(options = {}) {
    const {
      especesComptees,
      confirmCashCount = true,
      motifEcartCaisse,
      commercantId = null,
      forceClose = false,
      forceCloseMotif,
    } = options;
    return this.request('/caisse/session/fermer', {
      method: 'POST',
      body: JSON.stringify({
        especesComptees,
        confirmCashCount,
        ...(motifEcartCaisse != null && motifEcartCaisse !== ''
          ? { motifEcartCaisse }
          : {}),
        ...(forceClose ? { forceClose: true } : {}),
        ...(forceCloseMotif ? { forceCloseMotif } : {}),
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async getCaisseCatalogue(commercantId = null) {
    return this.request(`/caisse/catalogue${this._caisseQuery(commercantId)}`);
  }

  async lookupCaisseBarcode(code, commercantId = null) {
    const q = new URLSearchParams({ code: String(code) });
    const suffix = this._caisseQuery(commercantId);
    const join = suffix ? suffix.replace('?', '&') : '';
    return this.request(`/caisse/catalogue/lookup-barcode?${q.toString()}${join}`, {
      headers: { 'X-Caisse-POS': '1' },
    });
  }

  async getCaisseProduit(produitId, commercantId = null) {
    return this.request(`/caisse/produit/${produitId}${this._caisseQuery(commercantId)}`, {
      headers: { 'X-Caisse-POS': '1' },
    });
  }

  async getCaisseAEncaisser(commercantId = null) {
    return this.request(`/caisse/a-encaisser${this._caisseQuery(commercantId)}`);
  }

  async getCaisseHistorique(commercantId = null) {
    return this.request(`/caisse/historique${this._caisseQuery(commercantId)}`);
  }

  async verifierPromoCaisse(codePromo, sousTotal, commercantId = null) {
    return this.request('/caisse/promo/verifier', {
      method: 'POST',
      body: JSON.stringify({
        codePromo,
        sousTotal,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async creerVenteCaisse(payload, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request('/caisse/vente', options);
  }

  /** F2.4b1 — recherche clients (hors guest caisse) */
  async searchCaisseClients(q, commercantId = null) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(`/caisse/clients/search${qs ? `?${qs}` : ''}`);
  }

  /** F2.4b1 — catalogue + solde staff-for-client (0 apply) */
  async getCaisseFideliteCatalogue(clientUserId, commercantId = null) {
    const params = new URLSearchParams();
    if (clientUserId) params.set('clientUserId', clientUserId);
    if (commercantId) params.set('commercantId', commercantId);
    const qs = params.toString();
    return this.request(`/caisse/fidelite/catalogue${qs ? `?${qs}` : ''}`);
  }

  /** F2.4b1 — quote lecture seule (0 débit) */
  async postCaisseFideliteQuote(payload) {
    return this.request('/caisse/fidelite/quote', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async encaisserCaisse(payload, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request('/caisse/encaisser', options);
  }

  /** F2.4b2b — MAJ / retrait intention ticket (0 apply) */
  async patchTicketFideliteIntent(ticketId, payload = {}) {
    return this.request(`/caisse/ticket/${ticketId}/fidelite-intent`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getTicketsOuverts(commercantId = null) {
    return this.request(`/caisse/tickets-ouverts${this._caisseQuery(commercantId)}`);
  }

  async getTicketDetail(ticketId, commercantId = null) {
    return this.request(`/caisse/ticket/${ticketId}${this._caisseQuery(commercantId)}`);
  }

  async creerTicket(payload) {
    return this.request('/caisse/ticket', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** POS-L3-A1 — sync brouillon offline (ticket brouillon uniquement, flag serveur requis) */
  async syncOfflineDraftTicket(payload, idempotencyKey, commercantId = null) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Idempotency-Key': idempotencyKey },
    };
    return this.request(
      `/caisse/offline/draft-ticket${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async modifierTicket(ticketId, payload) {
    return this.request(`/caisse/ticket/${ticketId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async annulerTicket(ticketId) {
    return this.request(`/caisse/ticket/${ticketId}`, { method: 'DELETE' });
  }

  async annulerTicketPaye(ticketId, motif = '') {
    return this.request(`/caisse/ticket/${ticketId}/annuler`, {
      method: 'POST',
      body: JSON.stringify({ motif }),
    });
  }

  async rembourserTicket(ticketId, payload, commercantId = null) {
    const qs = this._caisseQuery(commercantId);
    return this.request(`/caisse/ticket/${ticketId}/rembourser${qs}`, {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async rembourserWebEncaisseEnCaisse(
    commandeId,
    payload,
    commercantId = null,
    { idempotencyKey } = {}
  ) {
    const qs = this._caisseQuery(commercantId);
    return this.request(`/caisse/commande/${commandeId}/rembourser-surplace${qs}`, {
      method: 'POST',
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
      body: JSON.stringify({
        ...payload,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async searchUnifiedReturns(params = {}, commercantId = null) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== '') {
        search.set(key, String(value));
      }
    });
    const qs = search.toString();
    const base = this._caisseQuery(commercantId);
    const suffix = base ? (qs ? `${base}&${qs}` : base) : qs ? `?${qs}` : '';
    return this.request(`/caisse/retours-unifies/search${suffix}`);
  }

  async getUnifiedReturnPreview(commandeId, commercantId = null) {
    return this.request(
      `/caisse/retours-unifies/commande/${commandeId}/preview${this._caisseQuery(commercantId)}`
    );
  }

  async searchRetailReturns({ q = '', barcode = '', days } = {}, commercantId = null) {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (barcode) params.set('barcode', barcode);
    if (days != null) params.set('days', String(days));
    const qs = params.toString();
    const base = this._caisseQuery(commercantId);
    const suffix = base ? (qs ? `${base}&${qs}` : base) : qs ? `?${qs}` : '';
    return this.request(`/caisse/retail/retours/search${suffix}`);
  }

  async getRetailReturnPreview(ticketId, commercantId = null) {
    return this.request(
      `/caisse/retail/retours/ticket/${ticketId}/preview${this._caisseQuery(commercantId)}`
    );
  }

  async executeRetailReturn(ticketId, payload, commercantId = null) {
    return this.request(
      `/caisse/retail/retours/ticket/${ticketId}/execute${this._caisseQuery(commercantId)}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  }

  /** L2-T2 — émission explicite d'addition / pré-note (journal fiscal). */
  async emitTicketAddition(ticketId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request(
      `/caisse/ticket/${ticketId}/addition${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async getTicketAddition(ticketId, commercantId, documentId = null) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (documentId) q.set('documentId', documentId);
    const qs = q.toString();
    return this.request(`/caisse/ticket/${ticketId}/addition${qs ? `?${qs}` : ''}`);
  }

  // —— L3-R0/R1 — Restaurant POS (plan de salle / services) ——
  async getRestaurantFloors(commercantId = null, { includeInactive = false } = {}) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (includeInactive) q.set('includeInactive', 'true');
    const qs = q.toString();
    return this.request(`/caisse/restaurant/floors${qs ? `?${qs}` : ''}`);
  }

  async createRestaurantFloor(payload) {
    return this.request('/caisse/restaurant/floors', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async patchRestaurantFloor(floorId, payload) {
    return this.request(`/caisse/restaurant/floors/${floorId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getRestaurantTables(commercantId = null, floorId = null) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (floorId) q.set('floorId', floorId);
    const qs = q.toString();
    return this.request(`/caisse/restaurant/tables${qs ? `?${qs}` : ''}`);
  }

  async createRestaurantTable(payload) {
    return this.request('/caisse/restaurant/tables', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async patchRestaurantTable(tableId, payload) {
    return this.request(`/caisse/restaurant/tables/${tableId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getRestaurantOccupancy(commercantId = null, { includeInactiveTables = false } = {}) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (includeInactiveTables) q.set('includeInactiveTables', 'true');
    const qs = q.toString();
    return this.request(`/caisse/restaurant/occupancy${qs ? `?${qs}` : ''}`);
  }

  async deleteRestaurantTable(tableId, commercantId = null) {
    return this.request(`/caisse/restaurant/tables/${tableId}${this._caisseQuery(commercantId)}`, {
      method: 'DELETE',
    });
  }

  async openRestaurantService(payload, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request('/caisse/restaurant/services/open', options);
  }

  async getRestaurantService(serviceId, commercantId = null) {
    return this.request(
      `/caisse/restaurant/services/${serviceId}${this._caisseQuery(commercantId)}`
    );
  }

  async listRestaurantActiveServices(commercantId = null) {
    return this.request(`/caisse/restaurant/services/active${this._caisseQuery(commercantId)}`);
  }

  async closeRestaurantService(serviceId, commercantId = null) {
    return this.request(`/caisse/restaurant/services/${serviceId}/close`, {
      method: 'POST',
      body: JSON.stringify(commercantId ? { commercantId } : {}),
    });
  }

  async listRestaurantServiceAdditions(serviceId, commercantId = null) {
    return this.request(
      `/caisse/restaurant/services/${serviceId}/additions${this._caisseQuery(commercantId)}`
    );
  }

  async getRestaurantCommandePrepLines(commandeId, commercantId = null) {
    return this.request(
      `/caisse/restaurant/commands/${commandeId}/prep-lines${this._caisseQuery(commercantId)}`
    );
  }

  async markRestaurantFloorLineServed(lineId, { expectedVersion, commercantId = null } = {}) {
    return this.request(
      `/caisse/restaurant/floor/lines/${lineId}/mark-served${this._caisseQuery(commercantId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ expectedVersion }),
      }
    );
  }

  async createRestaurantServiceAddition(serviceId, payload, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request(`/caisse/restaurant/services/${serviceId}/additions`, options);
  }

  async transferRestaurantService(serviceId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify({ ...payload, commercantId: commercantId || payload.commercantId }),
    };
    if (idempotencyKey) options.headers = { 'Idempotency-Key': idempotencyKey };
    return this.request(
      `/caisse/restaurant/services/${serviceId}/transfer${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async attachRestaurantTables(serviceId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify({ ...payload, commercantId: commercantId || payload.commercantId }),
    };
    if (idempotencyKey) options.headers = { 'Idempotency-Key': idempotencyKey };
    return this.request(
      `/caisse/restaurant/services/${serviceId}/attach-tables${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async detachRestaurantTable(serviceId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify({ ...payload, commercantId: commercantId || payload.commercantId }),
    };
    if (idempotencyKey) options.headers = { 'Idempotency-Key': idempotencyKey };
    return this.request(
      `/caisse/restaurant/services/${serviceId}/detach-table${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async mergeRestaurantOperational(serviceId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify({ ...payload, commercantId: commercantId || payload.commercantId }),
    };
    if (idempotencyKey) options.headers = { 'Idempotency-Key': idempotencyKey };
    return this.request(
      `/caisse/restaurant/services/${serviceId}/merge-operational${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async getRestaurantServiceTableGroup(serviceId, commercantId = null) {
    return this.request(
      `/caisse/restaurant/services/${serviceId}/table-group${this._caisseQuery(commercantId)}`
    );
  }

  async splitRestaurantAddition(serviceId, commandeId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify({ ...payload, commercantId: commercantId || payload.commercantId }),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request(
      `/caisse/restaurant/services/${serviceId}/additions/${commandeId}/split${this._caisseQuery(commercantId)}`,
      options
    );
  }

  /** L3-R2-C1a/C2 — cuisine / bar (KDS) */
  async listKitchenPrepStations(commercantId = null, { includeInactive = false } = {}) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (includeInactive) q.set('includeInactive', 'true');
    const qs = q.toString();
    return this.request(`/caisse/restaurant/kitchen/stations${qs ? `?${qs}` : ''}`);
  }

  async upsertKitchenPrepStation(body, commercantId = null) {
    return this.request('/caisse/restaurant/kitchen/stations', {
      method: 'POST',
      body: JSON.stringify({
        ...body,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async assignProductKitchenPrepStation(produitId, body, commercantId = null) {
    return this.request(`/caisse/restaurant/kitchen/products/${produitId}/station`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...body,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async assignProductKitchenPrepStationsBatch(body, commercantId = null) {
    return this.request('/caisse/restaurant/kitchen/products/station/batch', {
      method: 'POST',
      body: JSON.stringify({
        ...body,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async getRestaurantServiceClosePreview(serviceId, commercantId = null) {
    return this.request(
      `/caisse/restaurant/services/${serviceId}/close-preview${this._caisseQuery(commercantId)}`
    );
  }

  async sendKitchenCommandePrep(
    commandeId,
    { idempotencyKey, commercantId, sendMode, commandeProduitIds } = {}
  ) {
    return this.request(`/caisse/restaurant/kitchen/commands/${commandeId}/send`, {
      method: 'POST',
      body: JSON.stringify({
        idempotencyKey,
        ...(sendMode ? { sendMode } : {}),
        ...(commandeProduitIds?.length ? { commandeProduitIds } : {}),
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async getRestaurantCommandePrepSendState(commandeId, commercantId = null) {
    return this.request(
      `/caisse/restaurant/kitchen/commands/${commandeId}/send-state${this._caisseQuery(commercantId)}`
    );
  }

  async patchRestaurantPrepReleaseIntents(commandeId, { expectedPosRevision, intents, commercantId } = {}) {
    return this.request(`/caisse/restaurant/kitchen/commands/${commandeId}/prep-intents`, {
      method: 'PATCH',
      body: JSON.stringify({
        expectedPosRevision,
        intents,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async listOpenPosChannelOrders(commercantId = null) {
    return this.request(
      `/caisse/restaurant/channels/orders/open${this._caisseQuery(commercantId)}`
    );
  }

  async retryPosChannelPrep(commandeId, commercantId = null) {
    return this.request(
      `/caisse/restaurant/channels/orders/${commandeId}/retry-prep${this._caisseQuery(commercantId)}`,
      { method: 'POST', body: JSON.stringify({}) }
    );
  }

  async patchPosChannelOrderStatus(commandeId, statut, commercantId = null) {
    return this.request(
      `/caisse/restaurant/channels/orders/${commandeId}/status${this._caisseQuery(commercantId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ statut, ...(commercantId ? { commercantId } : {}) }),
      }
    );
  }

  /** RESA-TABLE-1 — réservations ↔ plan de salle */
  async getRestaurantResaPlanOverlay(commercantId = null) {
    return this.request(
      `/caisse/restaurant/reservations/plan-overlay${this._caisseQuery(commercantId)}`
    );
  }

  async getRestaurantResaDay(commercantId = null, { businessDayKey } = {}) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (businessDayKey) q.set('businessDayKey', businessDayKey);
    const qs = q.toString();
    return this.request(`/caisse/restaurant/reservations/day${qs ? `?${qs}` : ''}`);
  }

  async assignRestaurantResaTable(reservationId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify({ ...payload, commercantId: commercantId || payload.commercantId }),
    };
    if (idempotencyKey) options.headers = { 'Idempotency-Key': idempotencyKey };
    return this.request(
      `/caisse/restaurant/reservations/${reservationId}/assign-table${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async checkInRestaurantResa(reservationId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify({ ...payload, commercantId: commercantId || payload.commercantId }),
    };
    if (idempotencyKey) options.headers = { 'Idempotency-Key': idempotencyKey };
    return this.request(
      `/caisse/restaurant/reservations/${reservationId}/check-in${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async markRestaurantResaNoShow(reservationId, { raison, commercantId } = {}) {
    return this.request(
      `/caisse/restaurant/reservations/${reservationId}/no-show${this._caisseQuery(commercantId)}`,
      { method: 'POST', body: JSON.stringify({ raison, ...(commercantId ? { commercantId } : {}) }) }
    );
  }

  async cancelRestaurantResaFromPos(reservationId, { raison, commercantId } = {}) {
    return this.request(
      `/caisse/restaurant/reservations/${reservationId}/cancel${this._caisseQuery(commercantId)}`,
      { method: 'POST', body: JSON.stringify({ raison, ...(commercantId ? { commercantId } : {}) }) }
    );
  }

  async getKitchenStationQueue(stationId, commercantId = null, { statuses } = {}) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (statuses?.length) q.set('statuses', statuses.join(','));
    const qs = q.toString();
    return this.request(
      `/caisse/restaurant/kitchen/stations/${stationId}/queue${qs ? `?${qs}` : ''}`
    );
  }

  async patchKitchenPrepLineStatus(lineId, { toStatus, expectedVersion, commercantId } = {}) {
    return this.request(`/caisse/restaurant/kitchen/lines/${lineId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        toStatus,
        expectedVersion,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  /** L3-R2-C3 — impression restaurant (file métier) */
  async listPrintStationAssignments(commercantId = null, { stationId } = {}) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (stationId) q.set('stationId', stationId);
    const qs = q.toString();
    return this.request(`/caisse/restaurant/print/assignments${qs ? `?${qs}` : ''}`);
  }

  async listPrintJobs(commercantId = null, { limit, status } = {}) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (limit) q.set('limit', String(limit));
    if (status) q.set('status', status);
    const qs = q.toString();
    return this.request(`/caisse/restaurant/print/jobs${qs ? `?${qs}` : ''}`);
  }

  async sendPrintTestTicket({ hardwareDeviceId, idempotencyKey, commercantId, processImmediately = true }) {
    return this.request('/caisse/restaurant/print/test', {
      method: 'POST',
      body: JSON.stringify({
        hardwareDeviceId,
        idempotencyKey,
        processImmediately,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async upsertPrintStationAssignment(payload, commercantId = null) {
    return this.request('/caisse/restaurant/print/assignments', {
      method: 'PUT',
      body: JSON.stringify({
        ...payload,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async getPrintSupervision(commercantId = null) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    const qs = q.toString();
    return this.request(`/caisse/restaurant/print/supervision${qs ? `?${qs}` : ''}`);
  }

  async reprintPrintJob(jobId, { idempotencyKey, commercantId } = {}) {
    return this.request(`/caisse/restaurant/print/jobs/${jobId}/reprint`, {
      method: 'POST',
      body: JSON.stringify({
        idempotencyKey,
        ...(commercantId ? { commercantId } : {}),
      }),
    });
  }

  async requeuePrintJob(jobId, commercantId = null) {
    return this.request(`/caisse/restaurant/print/jobs/${jobId}/requeue`, {
      method: 'POST',
      body: JSON.stringify(commercantId ? { commercantId } : {}),
    });
  }

  async listTicketAdditions(ticketId, commercantId) {
    return this.request(`/caisse/ticket/${ticketId}/additions${this._caisseQuery(commercantId)}`);
  }

  async listFiscalDocuments(commercantId, { limit } = {}) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (limit) q.set('limit', String(limit));
    const qs = q.toString();
    return this.request(`/caisse/fiscal/documents${qs ? `?${qs}` : ''}`);
  }

  async correctTicketAddition(ticketId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request(
      `/caisse/ticket/${ticketId}/addition/corriger${this._caisseQuery(commercantId)}`,
      options
    );
  }

  async cancelTicketAddition(ticketId, payload, { idempotencyKey, commercantId } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request(
      `/caisse/ticket/${ticketId}/addition/annuler${this._caisseQuery(commercantId)}`,
      options
    );
  }

  /** L2-T3 — clôtures fiscales (lecture + déclenchement ; montants immuables côté API). */
  async listFiscalClosures(commercantId, closureType) {
    const q = new URLSearchParams();
    if (commercantId) q.set('commercantId', commercantId);
    if (closureType) q.set('closureType', closureType);
    const qs = q.toString();
    return this.request(`/caisse/fiscal/closures${qs ? `?${qs}` : ''}`);
  }

  async getFiscalClosure(closureType, periodKey, commercantId) {
    return this.request(
      `/caisse/fiscal/closures/${encodeURIComponent(closureType)}/${encodeURIComponent(periodKey)}${this._caisseQuery(commercantId)}`
    );
  }

  async closeFiscalDaily(payload, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request('/caisse/fiscal/closures/daily', options);
  }

  async closeFiscalMonthly(payload, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request('/caisse/fiscal/closures/monthly', options);
  }

  async closeFiscalAnnual(payload, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request('/caisse/fiscal/closures/annual', options);
  }

  /** L2-T4 — archives fiscales (génération / liste / téléchargement). */
  async listFiscalArchives(commercantId) {
    return this.request(`/caisse/fiscal/archives${this._caisseQuery(commercantId)}`);
  }

  async generateFiscalArchive(payload, { idempotencyKey } = {}) {
    const options = {
      method: 'POST',
      body: JSON.stringify(payload),
    };
    if (idempotencyKey) {
      options.headers = { 'Idempotency-Key': idempotencyKey };
    }
    return this.request('/caisse/fiscal/archives', options);
  }

  async downloadFiscalArchive(archiveId, commercantId) {
    const token = this.getAccessToken();
    const response = await fetch(
      `${this.baseURL}/caisse/fiscal/archives/${encodeURIComponent(archiveId)}/download${this._caisseQuery(commercantId)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!response.ok) {
      let message = 'Téléchargement de l\'archive fiscale impossible';
      try {
        const err = await response.json();
        if (err?.error || err?.message) message = err.error || err.message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] || `fiscal-archive-${archiveId}.zip`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return { sha256: response.headers.get('X-Archive-Sha256'), completeness: response.headers.get('X-Completeness-Status') };
  }

  async rembourserCommande(commandeId, payload) {
    return this.request(`/commandes/${commandeId}/rembourser`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async rembourserRendezVous(rendezVousId, payload) {
    return this.request(`/rendez-vous/${rendezVousId}/rembourser`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async downloadCommandeFacture(commandeId) {
    const token = this.getAccessToken();
    const response = await fetch(`${this.baseURL}/commandes/${commandeId}/facture`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      let message = 'Téléchargement de la facture impossible';
      try {
        const err = await response.json();
        if (err?.error || err?.message) message = err.error || err.message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] || `facture-${commandeId}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async downloadCommandeAvoir(commandeId, avoirId) {
    const token = this.getAccessToken();
    const response = await fetch(
      `${this.baseURL}/commandes/${commandeId}/avoirs/${avoirId}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!response.ok) {
      let message = 'Téléchargement de l\'avoir impossible';
      try {
        const err = await response.json();
        if (err?.error || err?.message) message = err.error || err.message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] || `avoir-${avoirId}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // --- Stripe Connect Phase B (onboarding uniquement — hors paiements) ---

  async getStripeConnectStatus(commercantId) {
    const qs = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/stripe-connect/status${qs}`);
  }

  async createStripeConnectAccount(commercantId) {
    return this.request('/stripe-connect/account', {
      method: 'POST',
      body: JSON.stringify({ commercantId }),
    });
  }

  async createStripeConnectOnboardingLink(commercantId) {
    return this.request('/stripe-connect/onboarding-link', {
      method: 'POST',
      body: JSON.stringify({ commercantId }),
    });
  }

  async createStripeConnectUpdateLink(commercantId) {
    return this.request('/stripe-connect/update-link', {
      method: 'POST',
      body: JSON.stringify({ commercantId }),
    });
  }

  async syncStripeConnect(commercantId) {
    return this.request('/stripe-connect/sync', {
      method: 'POST',
      body: JSON.stringify({ commercantId }),
    });
  }

  async ajusterPoidsLignesCommande(commandeId, lignes) {
    return this.request(`/commandes/${commandeId}/lignes-poids`, {
      method: 'PATCH',
      body: JSON.stringify({ lignes }),
    });
  }

  /** I2 — marquer une ligne CommandeProduit indisponible (API I1). */
  async marquerLigneIndisponible(commandeId, ligneId) {
    return this.request(`/commandes/${commandeId}/lignes/${ligneId}/indisponible`, {
      method: 'POST',
    });
  }

  async getCaisseKpi(params = {}) {
    const qs = new URLSearchParams();
    if (params.commercantId) qs.set('commercantId', params.commercantId);
    if (params.dateDebut) qs.set('dateDebut', params.dateDebut);
    if (params.dateFin) qs.set('dateFin', params.dateFin);
    if (params.sessionId) qs.set('sessionId', params.sessionId);
    if (params.vendeurId) qs.set('vendeurId', params.vendeurId);
    if (params.canalVente) qs.set('canalVente', params.canalVente);
    if (params.commercantIds) qs.set('commercantIds', params.commercantIds);
    const query = qs.toString();
    return this.request(`/caisse/kpi${query ? '?' + query : ''}`);
  }

  // --- Staff ---

  async getStaff(commercantId) {
    const params = commercantId ? `?commercantId=${commercantId}` : '';
    return this.request(`/staff${params}`);
  }

  async getPermissionsConfig() {
    return this.request('/staff/permissions-config');
  }

  async createStaff(data) {
    return this.request('/staff', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStaff(userId, data) {
    return this.request(`/staff/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deactivateStaff(userId) {
    return this.request(`/staff/${userId}/deactivate`, { method: 'PATCH' });
  }

  async activateStaff(userId) {
    return this.request(`/staff/${userId}/activate`, { method: 'PATCH' });
  }

  // --- Mon équipe (LOT1) ---

  async getEquipe(commercantId) {
    const params = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/equipe${params}`);
  }

  async getEquipeMember(memberId, commercantId) {
    const params = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/equipe/${encodeURIComponent(memberId)}${params}`);
  }

  async createEquipeMember(data, commercantId) {
    const params = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/equipe${params}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEquipeMember(memberId, data, commercantId) {
    const params = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/equipe/${encodeURIComponent(memberId)}${params}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async setEquipePrestations(memberId, prestationIds, commercantId) {
    const params = commercantId ? `?commercantId=${encodeURIComponent(commercantId)}` : '';
    return this.request(`/equipe/${encodeURIComponent(memberId)}/prestations${params}`, {
      method: 'PUT',
      body: JSON.stringify({ prestationIds }),
    });
  }

  // --- Praticiens / Instructeurs ---

  async getInstructeurs(commercantId, { actif } = {}) {
    const params = new URLSearchParams();
    if (actif === true) params.set('actif', 'true');
    if (actif === false) params.set('actif', 'false');
    const qs = params.toString();
    return this.request(`/instructeurs/commercant/${commercantId}${qs ? `?${qs}` : ''}`);
  }

  async getPrestationsPersonnelForInstructeurs(commercantId) {
    return this.request(`/instructeurs/commercant/${commercantId}/prestations-personnel`);
  }

  async createInstructeur(commercantId, data) {
    return this.request(`/instructeurs/commercant/${commercantId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInstructeur(instructeurId, data) {
    return this.request(`/instructeurs/${instructeurId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deactivateInstructeur(instructeurId) {
    return this.request(`/instructeurs/${instructeurId}/deactivate`, { method: 'PATCH' });
  }

  async activateInstructeur(instructeurId) {
    return this.request(`/instructeurs/${instructeurId}/activate`, { method: 'PATCH' });
  }

  // --- L4b-P1 Planning praticien (Admin) ---

  async getInstructeurHorairesConfig(instructeurId) {
    return this.request(`/instructeurs/${instructeurId}/horaires-config`);
  }

  async updateInstructeurModeHoraires(instructeurId, modeHoraires) {
    return this.request(`/instructeurs/${instructeurId}/mode-horaires`, {
      method: 'PUT',
      body: JSON.stringify({ modeHoraires }),
    });
  }

  async createInstructeurHoraire(instructeurId, data) {
    return this.request(`/instructeurs/${instructeurId}/horaires`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInstructeurHoraire(instructeurId, horaireId, data) {
    return this.request(`/instructeurs/${instructeurId}/horaires/${horaireId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteInstructeurHoraire(instructeurId, horaireId) {
    return this.request(`/instructeurs/${instructeurId}/horaires/${horaireId}`, {
      method: 'DELETE',
    });
  }

  async getInstructeurIndisponibilites(instructeurId, { actif } = {}) {
    const params = new URLSearchParams();
    if (actif === true) params.set('actif', 'true');
    if (actif === false) params.set('actif', 'false');
    const qs = params.toString();
    return this.request(
      `/instructeurs/${instructeurId}/indisponibilites${qs ? `?${qs}` : ''}`,
    );
  }

  async createInstructeurIndisponibilite(instructeurId, data) {
    return this.request(`/instructeurs/${instructeurId}/indisponibilites`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInstructeurIndisponibilite(instructeurId, indisponibiliteId, data) {
    return this.request(
      `/instructeurs/${instructeurId}/indisponibilites/${indisponibiliteId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    );
  }

  async deleteInstructeurIndisponibilite(instructeurId, indisponibiliteId) {
    return this.request(
      `/instructeurs/${instructeurId}/indisponibilites/${indisponibiliteId}`,
      { method: 'DELETE' },
    );
  }

  async getInstructeurHoraireDateExceptions(instructeurId, { from, to } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return this.request(
      `/instructeurs/${instructeurId}/horaires-exceptions${qs ? `?${qs}` : ''}`,
    );
  }

  async upsertInstructeurHoraireDateException(instructeurId, dateParis, plages) {
    return this.request(`/instructeurs/${instructeurId}/horaires-exceptions/${dateParis}`, {
      method: 'PUT',
      body: JSON.stringify({ plages }),
    });
  }

  async deleteInstructeurHoraireDateException(instructeurId, dateParis) {
    return this.request(`/instructeurs/${instructeurId}/horaires-exceptions/${dateParis}`, {
      method: 'DELETE',
    });
  }

  async getInstructeurPlanningAnomalies(instructeurId) {
    return this.request(`/instructeurs/${instructeurId}/planning-anomalies`);
  }

  async getSiteChrome() {
    return this.request('/admin/site-chrome');
  }

  async updateSiteChrome(data) {
    return this.request('/admin/site-chrome', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getSitePages() {
    return this.request('/admin/site-pages');
  }

  async getSitePage(slug) {
    return this.request(`/admin/site-pages/${slug}`);
  }

  async patchSitePageEnabled(slug, enabled) {
    return this.request(`/admin/site-pages/${slug}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  }

  async saveSitePageDraft(slug, data) {
    return this.request(`/admin/site-pages/${slug}/draft`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async previewSitePage(slug, markdown) {
    return this.request(`/admin/site-pages/${slug}/preview`, {
      method: 'POST',
      body: JSON.stringify({ markdown }),
    });
  }

  async publishSitePage(slug, options = {}) {
    return this.request(`/admin/site-pages/${slug}/publish`, {
      method: 'POST',
      body: JSON.stringify({
        requiresReacceptance: options.requiresReacceptance === true,
      }),
    });
  }

  async restoreSitePageRevision(slug, revisionId) {
    return this.request(`/admin/site-pages/${slug}/revisions/${revisionId}/restore-draft`, {
      method: 'POST',
    });
  }

  async getProLeads() {
    return this.request('/admin/pro-leads');
  }

  async createProLead(data) {
    return this.request('/admin/pro-leads', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProLead(id) {
    return this.request(`/admin/pro-leads/${id}`);
  }

  async markProLeadContacted(id) {
    return this.request(`/admin/pro-leads/${id}/contacted`, {
      method: 'POST',
    });
  }

  async updateProLead(id, data) {
    return this.request(`/admin/pro-leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ── Gestion NR2J (Super Admin) ──────────────────────────────────────────
  async getNr2jDashboard() {
    return this.request('/admin/nr2j/dashboard');
  }

  async getNr2jProspectionKpis(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const query = qs.toString();
    return this.request(`/admin/nr2j/prospection-kpis${query ? `?${query}` : ''}`);
  }

  async listNr2jOrganizations(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const query = qs.toString();
    return this.request(`/admin/nr2j/organizations${query ? `?${query}` : ''}`);
  }

  async createNr2jOrganization(data) {
    return this.request('/admin/nr2j/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getNr2jOrganization(id) {
    return this.request(`/admin/nr2j/organizations/${id}`);
  }

  async updateNr2jOrganization(id, data) {
    return this.request(`/admin/nr2j/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async convertNr2jOrganizationToClient(id) {
    return this.request(`/admin/nr2j/organizations/${id}/convert-to-client`, {
      method: 'POST',
    });
  }

  async archiveNr2jOrganization(id) {
    return this.request(`/admin/nr2j/organizations/${id}/archive`, {
      method: 'POST',
    });
  }

  async deleteNr2jOrganization(id) {
    return this.request(`/admin/nr2j/organizations/${id}`, {
      method: 'DELETE',
    });
  }

  async reactivateNr2jOrganization(id) {
    return this.request(`/admin/nr2j/organizations/${id}/reactivate`, {
      method: 'POST',
    });
  }

  async listNr2jOrganizationsForCommercant(commercantId) {
    return this.request(`/admin/nr2j/commercants/${commercantId}/organizations`);
  }

  async ensureNr2jOrganizationForProLead(proLeadId) {
    return this.request(`/admin/nr2j/pro-leads/${proLeadId}/ensure-organization`, {
      method: 'POST',
    });
  }

  async createNr2jContact(organizationId, data) {
    return this.request(`/admin/nr2j/organizations/${organizationId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNr2jContact(contactId, data) {
    return this.request(`/admin/nr2j/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNr2jContact(contactId) {
    return this.request(`/admin/nr2j/contacts/${contactId}`, {
      method: 'DELETE',
    });
  }

  async getNr2jBankSettings() {
    return this.request('/admin/nr2j/platform-settings/bank');
  }

  async updateNr2jBankSettings(data) {
    return this.request('/admin/nr2j/platform-settings/bank', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async listNr2jTasks(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const query = qs.toString();
    return this.request(`/admin/nr2j/tasks${query ? `?${query}` : ''}`);
  }

  async planNr2jFollowUp(organizationId, data) {
    return this.request(`/admin/nr2j/organizations/${organizationId}/plan-follow-up`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createNr2jTask(organizationId, data) {
    return this.request(`/admin/nr2j/organizations/${organizationId}/tasks`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNr2jTask(taskId, data) {
    return this.request(`/admin/nr2j/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async completeNr2jTask(taskId) {
    return this.request(`/admin/nr2j/tasks/${taskId}/complete`, {
      method: 'POST',
    });
  }

  async listNr2jActivities(organizationId, params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const query = qs.toString();
    return this.request(
      `/admin/nr2j/organizations/${organizationId}/activities${query ? `?${query}` : ''}`,
    );
  }

  async linkNr2jCommercant(organizationId, commercantId) {
    return this.request(`/admin/nr2j/organizations/${organizationId}/commercants`, {
      method: 'POST',
      body: JSON.stringify({ commercantId }),
    });
  }

  async unlinkNr2jCommercant(organizationId, commercantId) {
    return this.request(
      `/admin/nr2j/organizations/${organizationId}/commercants/${commercantId}`,
      { method: 'DELETE' },
    );
  }

  async listNr2jCatalog(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const query = qs.toString();
    return this.request(`/admin/nr2j/catalog${query ? `?${query}` : ''}`);
  }

  async createNr2jCatalogItem(data) {
    return this.request('/admin/nr2j/catalog', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNr2jCatalogItem(id, data) {
    return this.request(`/admin/nr2j/catalog/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async listNr2jQuotes(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const query = qs.toString();
    return this.request(`/admin/nr2j/quotes${query ? `?${query}` : ''}`);
  }

  async createNr2jQuote(data) {
    return this.request('/admin/nr2j/quotes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getNr2jQuote(id) {
    return this.request(`/admin/nr2j/quotes/${id}`);
  }

  async patchNr2jQuoteVersion(quoteId, versionId, data) {
    return this.request(`/admin/nr2j/quotes/${quoteId}/versions/${versionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async downloadNr2jQuotePdf(quoteId, versionId, meta = {}) {
    const token = this.getAccessToken();
    const response = await fetch(
      `${this.baseURL}/admin/nr2j/quotes/${quoteId}/versions/${versionId}/pdf`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) {
      let message = 'Téléchargement du PDF impossible';
      try {
        const err = await response.json();
        if (err?.error?.message) message = err.error.message;
        else if (err?.message) message = err.message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const rawBlob = await response.blob();
    const blob =
      rawBlob.type === 'application/pdf'
        ? rawBlob
        : new Blob([rawBlob], { type: 'application/pdf' });
    const disposition = response.headers.get('content-disposition') || '';
    const headerName = response.headers.get('x-pdf-filename') || '';
    const match = disposition.match(/filename="([^"]+)"/i);
    const fromMeta =
      meta?.number && meta?.version != null
        ? `${meta.number}-v${meta.version}.pdf`
        : '';
    const filename =
      match?.[1] || headerName || fromMeta || `devis-${quoteId}.pdf`;
    const url = URL.createObjectURL(blob);
    const mode = meta?.mode === 'preview' ? 'preview' : 'download';
    try {
      if (mode === 'preview') {
        // Aperçu : ouvrir le blob PDF dans un onglet.
        // Ne pas utiliser noopener sur window.open(blob:) — blank page fréquent.
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } finally {
      // Laisser le navigateur charger le blob avant revoke (sinon aperçu blanc / échec).
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    return { filename };
  }

  async sendNr2jQuote(id) {
    return this.request(`/admin/nr2j/quotes/${id}/send`, { method: 'POST' });
  }

  async newNr2jQuoteVersion(id) {
    return this.request(`/admin/nr2j/quotes/${id}/new-version`, { method: 'POST' });
  }

  async acceptNr2jQuote(id, data = {}) {
    return this.request(`/admin/nr2j/quotes/${id}/accept`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async refuseNr2jQuote(id, data = {}) {
    return this.request(`/admin/nr2j/quotes/${id}/refuse`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelNr2jQuote(id, data = {}) {
    return this.request(`/admin/nr2j/quotes/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteNr2jQuote(id) {
    return this.request(`/admin/nr2j/quotes/${id}`, { method: 'DELETE' });
  }

  async deleteNr2jQuoteVersion(quoteId, versionId) {
    return this.request(`/admin/nr2j/quotes/${quoteId}/versions/${versionId}`, {
      method: 'DELETE',
    });
  }

  // ── NR2J facturation MVP ────────────────────────────────────────────────
  async listNr2jInvoices(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const query = qs.toString();
    return this.request(`/admin/nr2j/invoices${query ? `?${query}` : ''}`);
  }

  async createNr2jInvoice(data) {
    return this.request('/admin/nr2j/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getNr2jInvoice(id) {
    return this.request(`/admin/nr2j/invoices/${id}`);
  }

  async patchNr2jInvoice(id, data) {
    return this.request(`/admin/nr2j/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNr2jInvoice(id) {
    return this.request(`/admin/nr2j/invoices/${id}`, { method: 'DELETE' });
  }

  async issueNr2jInvoice(id) {
    return this.request(`/admin/nr2j/invoices/${id}/issue`, { method: 'POST' });
  }

  async downloadNr2jInvoicePdf(id, meta = {}) {
    const token = this.getAccessToken();
    const response = await fetch(`${this.baseURL}/admin/nr2j/invoices/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      let message = 'Téléchargement du PDF impossible';
      try {
        const err = await response.json();
        if (err?.error?.message) message = err.error.message;
        else if (err?.message) message = err.message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const headerName = response.headers.get('x-pdf-filename') || '';
    const match = disposition.match(/filename="([^"]+)"/i);
    const fromMeta = meta?.number ? `${meta.number}.pdf` : '';
    const filename = match?.[1] || headerName || fromMeta || `facture-${id}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return { filename };
  }

  async sendNr2jInvoice(id) {
    return this.request(`/admin/nr2j/invoices/${id}/send`, { method: 'POST' });
  }

  async addNr2jInvoicePayment(id, data) {
    return this.request(`/admin/nr2j/invoices/${id}/payments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markNr2jInvoicePaid(id, data = {}) {
    return this.request(`/admin/nr2j/invoices/${id}/mark-paid`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createNr2jInvoiceFromQuote(quoteId) {
    return this.request(`/admin/nr2j/quotes/${quoteId}/create-invoice`, {
      method: 'POST',
    });
  }

  async createNr2jCreditNote(invoiceId, data) {
    return this.request(`/admin/nr2j/invoices/${invoiceId}/credit-notes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getNr2jCreditNote(id) {
    return this.request(`/admin/nr2j/credit-notes/${id}`);
  }

  async patchNr2jCreditNote(id, data) {
    return this.request(`/admin/nr2j/credit-notes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNr2jCreditNote(id) {
    return this.request(`/admin/nr2j/credit-notes/${id}`, { method: 'DELETE' });
  }

  async issueNr2jCreditNote(id) {
    return this.request(`/admin/nr2j/credit-notes/${id}/issue`, { method: 'POST' });
  }

  async downloadNr2jCreditNotePdf(id, meta = {}) {
    const token = this.getAccessToken();
    const response = await fetch(`${this.baseURL}/admin/nr2j/credit-notes/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      let message = 'Téléchargement du PDF impossible';
      try {
        const err = await response.json();
        if (err?.error?.message) message = err.error.message;
        else if (err?.message) message = err.message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const headerName = response.headers.get('x-pdf-filename') || '';
    const match = disposition.match(/filename="([^"]+)"/i);
    const fromMeta = meta?.number ? `${meta.number}.pdf` : '';
    const filename = match?.[1] || headerName || fromMeta || `avoir-${id}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return { filename };
  }

  // ── NR2J contrats ────────────────────────────────────────────────────────
  async listNr2jContracts(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const query = qs.toString();
    return this.request(`/admin/nr2j/contracts${query ? `?${query}` : ''}`);
  }

  async createNr2jContract(data) {
    return this.request('/admin/nr2j/contracts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createNr2jContractFromQuote(quoteId, data) {
    const body = data && typeof data === 'object' ? data : {};
    return this.request(`/admin/nr2j/contracts/from-quote/${quoteId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getNr2jContract(id) {
    return this.request(`/admin/nr2j/contracts/${id}`);
  }

  async patchNr2jContract(id, data) {
    return this.request(`/admin/nr2j/contracts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async submitNr2jContract(id) {
    return this.request(`/admin/nr2j/contracts/${id}/submit`, {
      method: 'POST',
      body: '{}',
    });
  }

  async sendNr2jContract(id) {
    return this.request(`/admin/nr2j/contracts/${id}/send`, { method: 'POST' });
  }

  async cancelNr2jContract(id, data = {}) {
    return this.request(`/admin/nr2j/contracts/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ confirmed: true, ...data }),
    });
  }

  async downloadNr2jContractPdf(id, meta = {}) {
    const token = this.getAccessToken();
    const response = await fetch(`${this.baseURL}/admin/nr2j/contracts/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      let message = 'Téléchargement du PDF impossible';
      try {
        const err = await response.json();
        if (err?.error?.message) message = err.error.message;
        else if (err?.message) message = err.message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const rawBlob = await response.blob();
    const blob =
      rawBlob.type === 'application/pdf'
        ? rawBlob
        : new Blob([rawBlob], { type: 'application/pdf' });
    const disposition = response.headers.get('content-disposition') || '';
    const headerName = response.headers.get('x-pdf-filename') || '';
    const match = disposition.match(/filename="([^"]+)"/i);
    const fromMeta =
      meta?.number && meta?.version != null
        ? `${meta.number}-v${meta.version}.pdf`
        : meta?.number
          ? `${meta.number}.pdf`
          : '';
    const filename = match?.[1] || headerName || fromMeta || `contrat-${id}.pdf`;
    const namedBlob =
      typeof File !== 'undefined'
        ? new File([blob], filename, { type: 'application/pdf' })
        : blob;
    const url = URL.createObjectURL(namedBlob);
    const mode = meta?.mode === 'preview' ? 'preview' : 'download';
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      if (mode === 'preview') {
        a.target = '_blank';
        a.rel = 'noopener';
      }
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    return { filename };
  }

  async listNr2jContractTemplates() {
    return this.request('/admin/nr2j/contract-templates');
  }

  async listMerchantDocuments() {
    return this.request('/merchant/documents');
  }

  async getAdminNotifications({ limit = 20 } = {}) {
    const q = new URLSearchParams();
    if (limit) q.set('limit', String(limit));
    const qs = q.toString();
    return this.request(`/admin-notifications${qs ? `?${qs}` : ''}`);
  }

  async getAdminNotificationsUnreadCount() {
    return this.request('/admin-notifications/unread-count');
  }

  async markAdminNotificationRead(id) {
    return this.request(`/admin-notifications/${id}/read`, { method: 'POST' });
  }

  async markAllAdminNotificationsRead() {
    return this.request('/admin-notifications/read-all', { method: 'POST' });
  }

  async acceptMerchantQuote(id) {
    return this.request(`/merchant/documents/quotes/${id}/accept`, {
      method: 'POST',
      body: JSON.stringify({ confirmed: true }),
    });
  }

  async refuseMerchantQuote(id, decisionNote) {
    return this.request(`/merchant/documents/quotes/${id}/refuse`, {
      method: 'POST',
      body: JSON.stringify({
        confirmed: true,
        ...(decisionNote ? { decisionNote } : {}),
      }),
    });
  }

  async openMerchantDocumentPdf(type, id, meta = {}) {
    const token = this.getAccessToken();
    const disposition = meta?.mode === 'preview' ? 'inline' : 'attachment';
    const response = await fetch(
      `${this.baseURL}/merchant/documents/${type}/${id}/pdf?disposition=${disposition}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!response.ok) {
      let message = 'Ouverture du PDF impossible';
      try {
        const err = await response.json();
        if (err?.message) message = err.message;
        else if (err?.error?.message) message = err.error.message;
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    const rawBlob = await response.blob();
    const blob = rawBlob.type === 'application/pdf'
      ? rawBlob
      : new Blob([rawBlob], { type: 'application/pdf' });
    const dispositionHeader = response.headers.get('content-disposition') || '';
    const headerName = response.headers.get('x-pdf-filename') || '';
    const match = dispositionHeader.match(/filename="([^"]+)"/i);
    const filename = match?.[1] || headerName || meta?.number || 'document.pdf';
    const url = URL.createObjectURL(blob);
    const mode = meta?.mode === 'preview' ? 'preview' : 'download';
    try {
      const a = document.createElement('a');
      a.href = url;
      if (mode === 'preview') {
        a.target = '_blank';
      } else {
        a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
      }
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
    return { filename };
  }
}

export default new ApiService();
